import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Calculator,
  AlertCircle,
  Search,
  ChevronRight,
  Info,
  Calendar,
  Hash,
  User,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Upload,
  FileText,
  Download,
  Bot,
  Edit2,
  Save,
  SkipForward,
  Image,
  ArrowRightLeft,
  History,
  ChevronDown,
  RotateCw,
  UserCog
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { supabase } from '../../src/lib/supabase';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import {
  addDaysAndAdjust,
  addOneMonthAndAdjust,
  addOneMonthSubDaysAndAdjust,
  addMonthsAndAdjust,
  addMonthsSubDaysAndAdjust,
  getMonthDiff,
  diffInDays,
  formatDateString,
  ordenarFaturasBasePrevisao,
  paraDataLocal
} from '../../src/lib/janelaCobranca';
// Mesmo limiar do gate de justificativa de emissao fora do prazo: uma definicao so.
import { DIAS_CRITICO_ANTES_VENCIMENTO } from '../../src/lib/janelaFaturamento';
import { mesAnterior } from '../../src/lib/conferenciaFaturamento';
import {
  atrasoPorMes,
  faturaAtrasadaNaEquatorial,
  leituraPrevista,
  perfilDaUc,
  prazoCurto,
  preverFatura,
  type PrevisaoFatura
} from '../../src/lib/previsaoFaturaEquatorial';
import type { AjustesFeriados } from '../../src/lib/reguaPrazosAgrupamento';
import { normalizeCpfCnpj } from '../../src/lib/utils';
import { CAMPOS_ZERADOS_SEM_INJECAO, camposZeradosSemInjecao } from '../../src/lib/faturaSemInjecao';
import PularCalculoCampo, { ESTADO_PULO_INICIAL, type EstadoPulo } from './components/PularCalculoCampo';
import { propsFundoModal } from '../../src/lib/backdropModal';
import { mensagemUcDuplicada } from '../../src/lib/ucDuplicada';
import JanelaFaturamentoLista from './components/JanelaFaturamentoLista';
import { ChipEntradaRobo, ChipFaturaAtrasada, ChipImpressaNaHora, ChipMotivoRobo, ChipPrazoCurto, ChipStatusRateio } from './components/ChipsCobranca';
import AtualizarDadosTitularModal from './components/AtualizarDadosTitularModal';
import { podeEditarCadastroDaUc, semCamposCadastrais, erroDeCadastroTravado } from '../../src/lib/permissaoCadastro';
import { chaveData, classificarStatusRateio, dadosTitularMudaram, motivoDoRobo, ordenarMeses } from './lib/janelaFaturamentoLista';
import type { AlvoAtualizacao } from './lib/realtimeFaturas';

type ModoVisualizacao = 'semana' | 'dia' | 'lista';
const CHAVE_MODO_VISUALIZACAO = 'vigorhub_janela_faturamento_view';

interface PendentesCalculoModalProps {
  isOpen: boolean;
  onClose: () => void;
  pendentes: any[];
  historicoFaturas: any[];
  unidadesConsumidoras?: any[];
  /** Avisa o que a ação mexeu para a tela recarregar só isso (sem alvo = carga cheia). */
  onActionComplete: (alvo?: AlvoAtualizacao) => void;
  onOpenValidation: (faturaId: string, keepOpen?: boolean) => void;
}

const PendentesCalculoModal: React.FC<PendentesCalculoModalProps> = ({
  isOpen,
  onClose,
  pendentes,
  historicoFaturas,
  unidadesConsumidoras = [],
  onActionComplete,
  onOpenValidation
}) => {
  const { user, profile } = useAuth();
  const { addToast } = useToast();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonthFilter, setSelectedMonthFilter] = useState<string>('Todos');
  const [selectedAnalystFilter, setSelectedAnalystFilter] = useState<string>(() => {
    return localStorage.getItem('vigorhub_cobrancas_analyst_filter') || 'Todos';
  });
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem('vigorhub_cobrancas_analyst_filter', selectedAnalystFilter);
  }, [selectedAnalystFilter]);

  // Feriados cadastrados na tela (tabela `feriados`): a previsão da fatura conta dias úteis
  // com o mesmo calendário da régua do agrupamento. Vazia ou sem acesso = calendário padrão.
  const [ajustesFeriados, setAjustesFeriados] = useState<AjustesFeriados | undefined>(undefined);
  useEffect(() => {
    if (!isOpen) return;
    let ativo = true;
    supabase.from('feriados').select('data, tipo').then(({ data, error }) => {
      if (!ativo || error || !data?.length) return;
      setAjustesFeriados({
        incluir: data.filter((f: any) => f.tipo === 'inclui').map((f: any) => String(f.data).slice(0, 10)),
        excluir: data.filter((f: any) => f.tipo === 'exclui').map((f: any) => String(f.data).slice(0, 10)),
      });
    });
    return () => { ativo = false; };
  }, [isOpen]);

  const [skippingItem, setSkippingItem] = useState<any | null>(null);
  // Motivo do pulo, já conferido contra a injeção da conta (PularCalculoCampo).
  const [estadoPulo, setEstadoPulo] = useState<EstadoPulo>(ESTADO_PULO_INICIAL);
  const limparMotivoSkip = () => setEstadoPulo(ESTADO_PULO_INICIAL);
  const [loading, setLoading] = useState(false);

  // Upload States
  const fileInputRef = useRef<HTMLInputElement>(null);
  const backdropMouseDownRef = useRef(false);
  const rescheduleBackdropMouseDownRef = useRef(false);
  const [uploadingForUc, setUploadingForUc] = useState<string | null>(null);
  const [enqueuingForUc, setEnqueuingForUc] = useState<string | null>(null);
  const [queueStatusByUc, setQueueStatusByUc] = useState<Map<string, any>>(new Map());
  const [historicoRoboItem, setHistoricoRoboItem] = useState<any | null>(null);
  // Cobrança cuja busca a Equatorial recusou por cadastro errado (CPF/CNPJ ou nascimento).
  const [corrigindoTitular, setCorrigindoTitular] = useState<{ item: any; tentativa: any } | null>(null);

  // Kanban & Daily View States
  const [viewMode, setViewMode] = useState<ModoVisualizacao>(() => {
    try {
      const salvo = localStorage.getItem(CHAVE_MODO_VISUALIZACAO);
      return salvo === 'dia' || salvo === 'lista' ? salvo : 'semana';
    } catch {
      return 'semana';
    }
  });

  useEffect(() => {
    try { localStorage.setItem(CHAVE_MODO_VISUALIZACAO, viewMode); } catch { /* sem storage: só não lembra */ }
  }, [viewMode]);
  const [selectedDay, setSelectedDay] = useState<Date>(new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // get Monday
    return new Date(d.setDate(diff));
  });

  const [analysts, setAnalysts] = useState<any[]>([]);
  const [rescheduleItem, setRescheduleItem] = useState<any | null>(null);
  const [viewingReschedule, setViewingReschedule] = useState<any | null>(null);
  const [newScheduleDate, setNewScheduleDate] = useState('');
  const [motivoReagendamento, setMotivoReagendamento] = useState('');
  const [comprovanteFile, setComprovanteFile] = useState<File | null>(null);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  // Transfer states
  const [transferItem, setTransferItem] = useState<any | null>(null);
  const [transferTargetId, setTransferTargetId] = useState('');
  const [transferring, setTransferring] = useState(false);

  const canTransferItem = (item: any): boolean => {
    if (!profile) return false;
    if (profile.role === 'adm' || profile.role === 'gerente') return true;
    if (profile.role === 'analista') return item.responsavel_id === profile.id;
    return false;
  };

  const toggleExpand = (uc: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(uc)) next.delete(uc);
      else next.add(uc);
      return next;
    });
  };

  // Fetch Analysts for distribution
  useEffect(() => {
    const fetchAnalysts = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, full_name')
          .eq('role', 'analista')
          .contains('permissions', '["Receber Cobranças pendentes / Faturamento"]');

        if (!error && data) {
          setAnalysts(data);
        }
      } catch (e) {
        console.error('Error fetching analysts:', e);
      }
    };
    fetchAnalysts();
  }, []);

  // Edit UC States
  const [editingUcId, setEditingUcId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<any>({});
  const [savingUc, setSavingUc] = useState(false);
  const [uploadSuccessUc, setUploadSuccessUc] = useState<string | null>(null);

  // Histórico Manual (última fatura informada manualmente para prever a janela)
  const [editingHistUcId, setEditingHistUcId] = useState<number | null>(null);
  const [histFormData, setHistFormData] = useState<any>({});
  const [savingHist, setSavingHist] = useState(false);

  const handleSaveHistorico = async (ucId: number) => {
    setSavingHist(true);
    try {
      const mes = (histFormData.hist_manual_mes || '').trim().toUpperCase();
      const ano = (histFormData.hist_manual_ano || '').trim();
      const payload = {
        hist_manual_mes_referencia: mes && ano ? `${mes}/${ano}` : null,
        hist_manual_leitura_anterior: histFormData.hist_manual_leitura_anterior || null,
        hist_manual_leitura_atual: histFormData.hist_manual_leitura_atual || null,
        hist_manual_proxima_leitura: histFormData.hist_manual_proxima_leitura || null,
        hist_manual_data_apresentacao: histFormData.hist_manual_data_apresentacao || null,
        hist_manual_data_vencimento: histFormData.hist_manual_data_vencimento || null,
      };

      const { error } = await supabase.from('unidades_consumidoras').update(payload).eq('id', ucId);
      if (error) throw error;

      addToast('Dados da última fatura salvos. A janela será recalculada.', 'success');
      setEditingHistUcId(null);
      setHistFormData({});
      onActionComplete({ ucIds: [ucId] });
    } catch (e: any) {
      addToast('Erro ao salvar os dados da última fatura.', 'error');
      console.error(e);
    } finally {
      setSavingHist(false);
    }
  };

  const handleSaveUc = async (ucId: string, isNew: boolean = false) => {
    setSavingUc(true);
    try {
      const performSave = async (payloadData: any) => {
        if (isNew) {
          const finalPayload = { ...payloadData };
          delete finalPayload.id;
          // Usuário de empresa parceira só cadastra UC para a própria empresa (RLS já
          // reforça isso).
          if (profile?.role === 'empresa_parceira_admin' || profile?.role === 'empresa_parceira_operador') {
            finalPayload.empresa_parceira_id = profile.empresa_parceira_id;
          }
          return supabase.from('unidades_consumidoras').insert(finalPayload);
        } else {
          return supabase.from('unidades_consumidoras').update(payloadData).eq('id', ucId);
        }
      };

      // #6239: sem a permissão, nome/CPF/titular de UC em operação não vão no UPDATE
      // (os campos ficam travados na tela; o banco barraria).
      const dadosParaGravar = !isNew && !podeEditarCadastroDaUc(profile, editFormData)
        ? semCamposCadastrais(editFormData)
        : editFormData;
      let { error } = await performSave(dadosParaGravar);

      // Catch missing column error and retry without num_medidor
      if (error && error.message && error.message.includes('num_medidor') && error.message.includes('does not exist')) {
        console.warn('Attempting fallback save without num_medidor column...');
        const fallbackData = { ...dadosParaGravar };
        delete (fallbackData as any).num_medidor;
        const retry = await performSave(fallbackData);
        error = retry.error;
      }

      if (error) throw error;

      addToast(isNew ? 'Unidade Consumidora cadastrada com sucesso' : 'Dados da UC atualizados com sucesso', 'success');
      setEditingUcId(null);
      // UC nova muda o casamento UC ↔ rateio de várias cobranças: aí vale a carga cheia.
      onActionComplete(isNew ? undefined : { ucIds: [Number(ucId)] });
    } catch (e: any) {
      addToast(mensagemUcDuplicada(e) ?? (erroDeCadastroTravado(e) ? e.message : 'Erro ao atualizar UC'), 'error');
      console.error(e);
    } finally {
      setSavingUc(false);
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleItem || !newScheduleDate) return;
    if (!motivoReagendamento.trim()) {
      addToast('Por favor, informe o motivo do reagendamento.', 'warning');
      return;
    }
    if (!comprovanteFile) {
      addToast('Por favor, anexe o print da prova do reagendamento.', 'warning');
      return;
    }

    setLoading(true);
    try {
      const { fatura, uc, outro_numero_uc, nome, expectedMonth, cpf_cnpj } = rescheduleItem;
      const dataAgendada = new Date(newScheduleDate).toISOString().split('T')[0];
      const cleanUcVal = String(outro_numero_uc || uc || '').replace(/[.\-\s]/g, '');
      const userName = user?.user_metadata?.full_name || user?.email || 'Usuário Desconhecido';
      const nowIso = new Date().toISOString();

      // Upload screenshot to Supabase Storage
      const fileExt = comprovanteFile.name.split('.').pop() || 'jpg';
      const fileName = `reagendamento-${cleanUcVal}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('comprovantes')
        .upload(fileName, comprovanteFile);

      if (uploadError) {
        throw new Error('Falha ao enviar a imagem de prova: ' + uploadError.message);
      }

      const { data: publicUrlData } = supabase.storage
        .from('comprovantes')
        .getPublicUrl(fileName);

      const publicUrl = publicUrlData.publicUrl;

      let faturaAlterada: number;
      if (fatura && fatura.id) {
        // Atualiza a fatura existente
        const { error } = await supabase
          .from('faturas')
          .update({
            data_cobranca_agendada: dataAgendada,
            motivo_reagendamento: motivoReagendamento.trim(),
            comprovante_reagendamento_url: publicUrl,
            reagendamento_em: nowIso,
            reagendamento_por_nome: userName,
            responsavel_cobranca_id: rescheduleItem.responsavel_id
          })
          .eq('id', fatura.id);
        if (error) throw error;
        faturaAlterada = fatura.id;
      } else {
        // Insere esboço de fatura se for prevista
        const { data: esboco, error } = await supabase.from('faturas').insert({
          uc_codigo: outro_numero_uc || uc,
          cliente_nome: nome,
          cliente_cpf_cnpj: cpf_cnpj,
          mes_referencia: expectedMonth,
          status_fatura: 'Aguardando Upload',
          classificacao: 'NORMAL',
          data_cobranca_agendada: dataAgendada,
          motivo_reagendamento: motivoReagendamento.trim(),
          comprovante_reagendamento_url: publicUrl,
          reagendamento_em: nowIso,
          reagendamento_por_nome: userName,
          responsavel_cobranca_id: rescheduleItem.responsavel_id
        }).select('id').single();
        if (error) throw error;
        faturaAlterada = esboco.id;
      }

      addToast('Cobrança reagendada com sucesso', 'success');
      setRescheduleItem(null);
      setNewScheduleDate('');
      setMotivoReagendamento('');
      setComprovanteFile(null);
      onActionComplete({ faturaIds: [faturaAlterada] });
    } catch (err: any) {
      console.error('Error rescheduling:', err);
      addToast(err.message || 'Erro ao reagendar cobrança', 'error');
    } finally {
      setLoading(false);
    }
  };

  const cleanUC = (uc?: any) => {
    if (typeof uc !== 'string' && typeof uc !== 'number') return '';
    return String(uc).replace(/[.\-\s]/g, '').replace(/^0+/, '');
  };

  // 1. Generate Predictive Map from history loaded in memory (no fetching needed)
  const predictiveDataMap = useMemo(() => {
    if (!historicoFaturas || historicoFaturas.length === 0) return {};

    // Agrupa por UC (ignorando AVULSO e placeholders "Aguardando Upload"). A escolha
    // da fatura base é feita depois, por item, em ordenarFaturasBasePrevisao.
    const sorted = [...historicoFaturas]
      .filter(f =>
        f.classificacao !== 'AVULSO' &&
        f.status_fatura !== 'Aguardando Upload' &&
        // Faturas sem nenhum dado de data (pular cálculo, placeholders) não servem para prever janela
        (f.leitura_atual || f.data_apresentacao || f.proxima_leitura)
      )
      .sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateB - dateA;
      });

    const map: Record<string, any[]> = {};
    for (const fatura of sorted) {
      const cleanCode = cleanUC(fatura.uc_codigo);
      if (cleanCode) {
        if (!map[cleanCode]) map[cleanCode] = [];
        map[cleanCode].push(fatura);
      }
    }
    return map;
  }, [historicoFaturas]);

  // 2. Fetch Webhook URL
  useEffect(() => {
    const fetchWebhookUrl = async () => {
      try {
        const { data } = await supabase
          .from('system_settings')
          .select('value')
          .eq('key', 'pdf_invoice_webhook_url')
          .single();
        if (data?.value) {
          setWebhookUrl(data.value);
        }
      } catch (error) {
        console.error('Error fetching webhook URL:', error);
      }
    };

    if (isOpen && !webhookUrl) {
      fetchWebhookUrl();
    }
  }, [isOpen, webhookUrl]);

  // 3. Polling do status da fila de download da Equatorial
  // Filtra por mês, não por UC: as ~2 mil UCs das cobranças iam inteiras no
  // `in.(...)` da URL e o servidor respondia 400 em toda consulta. A chave dos
  // meses (e não `pendentes`) é a dependência: as pendências mudam a cada
  // atualização em tempo real, e cada mudança refazia a consulta da fila.
  const chaveMesesFila = useMemo(() => [...new Set(
    pendentes.flatMap((p: any) => [p.expectedMonth, p.fatura?.mes_referencia]).filter(Boolean)
  )].sort().join('|'), [pendentes]);
  const refreshQueueRef = useRef<() => void>(() => {});
  const onActionCompleteRef = useRef(onActionComplete);
  onActionCompleteRef.current = onActionComplete;
  const unidadesConsumidorasRef = useRef(unidadesConsumidoras);
  unidadesConsumidorasRef.current = unidadesConsumidoras;
  const statusFilaAnteriorRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!isOpen) return;

    const fetchQueue = async () => {
      try {
        const meses = chaveMesesFila ? chaveMesesFila.split('|') : [];
        if (!meses.length) return;

        const PAGINA = 1000;
        const linhas: any[] = [];
        for (let offset = 0; ; offset += PAGINA) {
          const { data, error } = await supabase
            .from('fatura_download_queue')
            // cpf_cnpj/data_nascimento: dados usados na tentativa, para saber se o cadastro já foi corrigido.
            .select('id, uc_codigo, mes_referencia, status, tentativas, error_details, historico_execucoes, cpf_cnpj, data_nascimento')
            .in('mes_referencia', meses)
            // 'concluido' entra para que um sucesso recente esconda falhas antigas da mesma UC+mês.
            .in('status', ['pendente', 'processando', 'falhou', 'concluido'])
            .order('created_at', { ascending: false })
            .order('id', { ascending: false })
            .range(offset, offset + PAGINA - 1);
          if (error) throw error;
          linhas.push(...(data || []));
          if (!data || data.length < PAGINA) break;
        }

        // A mesma UC+mês pode ter várias linhas (reenfileiramentos): o status vem
        // da mais recente e o relatório junta as tentativas de todas.
        const map = new Map<string, any>();
        linhas.forEach((entry: any) => {
          const chave = `${entry.uc_codigo}:${entry.mes_referencia}`;
          const atual = map.get(chave);
          if (atual) {
            atual.historico_execucoes.push(...(entry.historico_execucoes || []));
          } else {
            map.set(chave, { ...entry, historico_execucoes: [...(entry.historico_execucoes || [])] });
          }
        });
        map.forEach((entry) => {
          entry.historico_execucoes.sort((a: any, b: any) => Date.parse(a.timestamp) - Date.parse(b.timestamp));
        });
        setQueueStatusByUc(map);

        // Tarefa que acabou de concluir: o robô gravou fatura nova (mês específico)
        // ou o histórico manual da UC (última disponível) — nenhum dos dois passa
        // pela tela, então a UC é recarregada aqui. A 1ª consulta só registra a base.
        const anterior = statusFilaAnteriorRef.current;
        const concluidasAgora = linhas.filter((l: any) =>
          l.status === 'concluido' && anterior.has(l.id) && anterior.get(l.id) !== 'concluido');
        statusFilaAnteriorRef.current = new Map(linhas.map((l: any) => [l.id, l.status]));
        if (concluidasAgora.length) {
          const norm = (v: any) => String(v ?? '').replace(/[.\-\s]/g, '').replace(/^0+/, '');
          const codigos = new Set(concluidasAgora.map((l: any) => norm(l.uc_codigo)));
          const ucIds = unidadesConsumidorasRef.current
            .filter((uc: any) => codigos.has(norm(uc.numero_uc)) || codigos.has(norm(uc.outro_numero_uc)))
            .map((uc: any) => Number(uc.id));
          onActionCompleteRef.current({ ucCodigos: [...codigos], ucIds });
        }
      } catch (erro) {
        console.error('[Equatorial] falha ao consultar a fila do robô:', erro);
      }
    };

    refreshQueueRef.current = () => { fetchQueue(); };
    fetchQueue();
    const interval = setInterval(fetchQueue, 30000);
    return () => clearInterval(interval);
  }, [isOpen, chaveMesesFila]);

  // 4. Setup Display Items
  const uniqueMonths = useMemo(() => {
    return ordenarMeses(pendentes.map(p => p.expectedMonth));
  }, [pendentes]);

  // Itens com janela, urgência e responsável resolvidos, SEM filtro de tela.
  // Os cards filtram abaixo (displayItems); a lista aplica os próprios filtros.
  const cobrancasMapeadas = useMemo(() => {
    const sortedAnalysts = [...analysts].sort((a, b) => (a.id || '').localeCompare(b.id || ''));

    const cleanUC = (uc?: any) => {
      if (typeof uc !== 'string' && typeof uc !== 'number') return '';
      return String(uc).replace(/[.\-\s]/g, '').replace(/^0+/, '');
    };

    const ucsMap = new Map<string, any>();
    unidadesConsumidoras.forEach(uc => {
      if (uc.numero_uc) {
        const norm1 = cleanUC(uc.numero_uc);
        if (norm1) ucsMap.set(norm1, uc);
      }
      if (uc.outro_numero_uc) {
        const norm2 = cleanUC(uc.outro_numero_uc);
        if (norm2) ucsMap.set(norm2, uc);
      }
    });

    const hojeIso = chaveData(new Date());

    // 1. Mapeia primeiro todos os itens para resolver os campos derivados (incluindo analista estável)
    const mapped = pendentes.map(p => {
      // "Aguardando Upload" é um placeholder de reagendamento, não uma fatura real com dados
      const hasFatura = !!p.fatura && p.fatura.status_fatura !== 'Aguardando Upload';
      const aguardandoUpload = !hasFatura && p.fatura?.status_fatura === 'Aguardando Upload' ? p.fatura : null;
      
      const itemUcCleanForRegistry = cleanUC(p.uc);
      const itemOutroUcCleanForRegistry = cleanUC(p.outro_numero_uc);
      const ucRegistryForItem = ucsMap.get(itemUcCleanForRegistry) || (itemOutroUcCleanForRegistry ? ucsMap.get(itemOutroUcCleanForRegistry) : undefined);

      const findPredictive = (p: any) => {
        const itemUcClean = itemUcCleanForRegistry;
        const itemOutroUcClean = itemOutroUcCleanForRegistry;
        const ucRegistry = ucRegistryForItem;

        const searchUc1 = cleanUC(ucRegistry?.numero_uc);
        const searchUc2 = cleanUC(ucRegistry?.outro_numero_uc);

        // Uma UC pode ter fatura real espalhada entre os dois números que a
        // identificam (numero_uc/outro_numero_uc) — ex.: o número lançado no
        // rateio tem só histórico velho isolado, enquanto o outro número da
        // mesma UC concentra as faturas recentes. Juntamos os candidatos de
        // todas as variantes antes de escolher: parar na primeira variante
        // com QUALQUER resultado (mesmo velho) faz a previsão de janela
        // ignorar a fatura mais recente cadastrada sob o outro número.
        const candidateKeys = Array.from(new Set([itemUcClean, itemOutroUcClean, searchUc1, searchUc2].filter(Boolean)));
        const merged = candidateKeys.flatMap(key => predictiveDataMap[key] || []);
        if (merged.length === 0) return null;

        const seenIds = new Set<any>();
        const deduped = merged.filter(f => {
          if (seenIds.has(f.id)) return false;
          seenIds.add(f.id);
          return true;
        });

        // Base = mês de referência mais próximo do previsto, não a última linha
        // criada (ver ordenarFaturasBasePrevisao).
        return ordenarFaturasBasePrevisao(deduped, p.expectedMonth);
      };
      const predictiveArray = findPredictive(p) || [];
      let baseFatura = hasFatura ? p.fatura : predictiveArray[0];
      const lastTwo = predictiveArray.slice(0, 2);

      // Sem nenhuma fatura real no histórico: usa os dados da última fatura
      // informados manualmente na UC (fallback) para ainda assim prever a janela.
      let isHistoricoManual = false;
      if (!hasFatura && !baseFatura && ucRegistryForItem && (
        ucRegistryForItem.hist_manual_data_apresentacao ||
        ucRegistryForItem.hist_manual_leitura_atual ||
        ucRegistryForItem.hist_manual_proxima_leitura
      )) {
        baseFatura = {
          mes_referencia: ucRegistryForItem.hist_manual_mes_referencia || p.expectedMonth,
          leitura_anterior: ucRegistryForItem.hist_manual_leitura_anterior,
          leitura_atual: ucRegistryForItem.hist_manual_leitura_atual,
          proxima_leitura: ucRegistryForItem.hist_manual_proxima_leitura,
          data_apresentacao: ucRegistryForItem.hist_manual_data_apresentacao,
          data_vencimento: ucRegistryForItem.hist_manual_data_vencimento,
        };
        isHistoricoManual = true;
      }

      let diasEntreLeituras: number | null = null;
      if (baseFatura && baseFatura.leitura_atual && baseFatura.leitura_anterior) {
        diasEntreLeituras = diffInDays(baseFatura.leitura_atual, baseFatura.leitura_anterior);
      } else if (lastTwo.length >= 2 && lastTwo[0].leitura_atual && lastTwo[1].leitura_atual) {
        diasEntreLeituras = diffInDays(lastTwo[0].leitura_atual, lastTwo[1].leitura_atual);
      }

      let inicioJanelaIso: Date | null = null;
      let fimJanelaIso: Date | null = null;
      let criticoJanelaIso: Date | null = null;
      let isIncompleto = false;
      let isPredictive = !hasFatura;
      // Leitura + dias úteis (src/lib/previsaoFaturaEquatorial.ts). Com fatura, só serve para
      // saber se ela chegou no prazo; sem fatura, é a janela prevista.
      let previsaoFatura: PrevisaoFatura | null = null;

      if (hasFatura) {
        inicioJanelaIso = baseFatura.leitura_atual ? addDaysAndAdjust(baseFatura.leitura_atual, 1) : null;
        fimJanelaIso = baseFatura.data_apresentacao ? new Date(baseFatura.data_apresentacao) : null;
        criticoJanelaIso = baseFatura.data_vencimento ? addDaysAndAdjust(baseFatura.data_vencimento, -DIAS_CRITICO_ANTES_VENCIMENTO) : null;
        isIncompleto = !baseFatura.leitura_atual || !baseFatura.data_apresentacao || !baseFatura.data_vencimento;
        previsaoFatura = preverFatura(baseFatura.leitura_atual, ajustesFeriados);
      } else if (baseFatura) {
        const diffMonths = getMonthDiff(p.expectedMonth, baseFatura.mes_referencia || p.expectedMonth);

        // Antes: início = dia seguinte à leitura (quando quase nenhuma fatura chegou, 1%) e
        // fim = apresentação do mês anterior + 1 mês (acertava 51%). Agora a fatura chega por
        // volta do 4º dia útil depois da leitura e é apresentada no 7º (acerta 83%). UC com
        // fatura impressa na hora nos últimos 3 meses: chega no 1º dia útil (perfilDaUc).
        const perfil = perfilDaUc(predictiveArray, p.expectedMonth, ajustesFeriados);
        previsaoFatura = preverFatura(leituraPrevista(baseFatura.proxima_leitura, diffMonths), ajustesFeriados, perfil);
        if (previsaoFatura) {
          inicioJanelaIso = paraDataLocal(previsaoFatura.chegada);
          fimJanelaIso = paraDataLocal(previsaoFatura.apresentacao);
        } else {
          // Base sem próxima leitura: sem como contar dias úteis, fica a conta antiga do fim.
          fimJanelaIso = baseFatura.data_apresentacao ? addMonthsAndAdjust(baseFatura.data_apresentacao, diffMonths) : null;
        }
        criticoJanelaIso = baseFatura.data_vencimento ? addMonthsSubDaysAndAdjust(baseFatura.data_vencimento, diffMonths, DIAS_CRITICO_ANTES_VENCIMENTO) : null;
        isIncompleto = !baseFatura.proxima_leitura || !baseFatura.data_apresentacao || !baseFatura.data_vencimento;
      }
      const atrasadaEquatorial = faturaAtrasadaNaEquatorial(previsaoFatura, hojeIso, hasFatura);
      // Limite de emissão até 2 dias depois de a fatura chegar: emitir assim que ela chegar.
      const prazoCurtoEmissao = !hasFatura && !!criticoJanelaIso && prazoCurto(previsaoFatura, chaveData(criticoJanelaIso));

      let status = 'normal'; // normal, warning, critical, sem_previsao
      
      if (!inicioJanelaIso && !fimJanelaIso) {
        status = 'sem_previsao';
      } else if (criticoJanelaIso && !isNaN(criticoJanelaIso.getTime())) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (today > criticoJanelaIso) {
          status = 'critical';
        } else {
          const daysDiff = diffInDays(criticoJanelaIso.toISOString(), today.toISOString());
          if (daysDiff !== null && daysDiff <= 2) {
            status = 'warning';
          }
        }
      }

      // --- KANBAN DISTRIBUTION LOGIC ---
      let targetDateIso = inicioJanelaIso;
      // Usa data agendada da fatura real ou do placeholder de reagendamento
      const dataAgendada = baseFatura?.data_cobranca_agendada ?? aguardandoUpload?.data_cobranca_agendada;
      if (dataAgendada) {
        targetDateIso = new Date(dataAgendada);
        // Corrige o fuso caso a string YYYY-MM-DD vire dia anterior no UTC
        targetDateIso = new Date(targetDateIso.getTime() + targetDateIso.getTimezoneOffset() * 60000);
      }

      // Shift weekends to next Monday
      if (targetDateIso && !isNaN(targetDateIso.getTime())) {
        const dayOfWeek = targetDateIso.getDay();
        if (dayOfWeek === 0) { // Sunday -> +1
          targetDateIso = new Date(targetDateIso.setDate(targetDateIso.getDate() + 1));
        } else if (dayOfWeek === 6) { // Saturday -> +2
          targetDateIso = new Date(targetDateIso.setDate(targetDateIso.getDate() + 2));
        }
      }

      // --- ATRIBUIÇÃO ESTÁVEL DE ANALISTA ---
      let responsavel_id = baseFatura?.responsavel_cobranca_id || null;
      let responsavel_nome = '';

      if (sortedAnalysts.length > 0) {
        if (!responsavel_id) {
          const cleanUc = String(p.uc || p.outro_numero_uc || '').replace(/\D/g, '');
          // Hash de string (não o valor numérico da UC direto: para UCs com até 15
          // dígitos isso equivalia a usar Number(cleanUc) % nºAnalistas, ou seja, a
          // PARIDADE do número da UC — e como a numeração de UC não é uniformemente
          // distribuída entre pares/ímpares, isso enviesava a distribuição de forma
          // sistemática (ex.: 54%/46% entre 2 analistas). O hash abaixo mistura todos
          // os dígitos antes do módulo, mantendo a atribuição estável (mesma UC →
          // sempre o mesmo analista) mas com distribuição equilibrada entre eles.
          let hashVal = 0;
          for (let i = 0; i < cleanUc.length; i++) {
            hashVal = (hashVal << 5) - hashVal + cleanUc.charCodeAt(i);
            hashVal |= 0;
          }
          const analystIndex = Math.abs(hashVal) % sortedAnalysts.length;
          const deterministicAnalyst = sortedAnalysts[analystIndex];
          responsavel_id = deterministicAnalyst.id;
          responsavel_nome = deterministicAnalyst.full_name;
        } else {
          const found = sortedAnalysts.find(a => a.id === responsavel_id);
          if (found) responsavel_nome = found.full_name;
        }
      }

      const hasManualReschedule = !!dataAgendada;
      const ucClean1 = cleanUC(p.uc);
      const ucClean2 = p.outro_numero_uc ? cleanUC(p.outro_numero_uc) : '';
      const ucData = ucsMap.get(ucClean1) || (ucClean2 ? ucsMap.get(ucClean2) : undefined) || null;

      return {
        ...p,
        // `status` do item de rateio é sobrescrito abaixo pela urgência da janela.
        statusRateio: p.status ?? null,
        hasFatura,
        inicioJanelaIso,
        fimJanelaIso,
        criticoJanelaIso,
        targetDateIso,
        hasManualReschedule,
        responsavel_id,
        responsavel_nome,
        isIncompleto,
        isPredictive,
        isHistoricoManual,
        status,
        baseFatura,
        diasEntreLeituras,
        previsaoFatura,
        atrasadaEquatorial,
        prazoCurto: prazoCurtoEmissao,
        uc_identificadora: p.uc,
        ucData,
      };
    });

    return mapped;
  }, [pendentes, predictiveDataMap, analysts, unidadesConsumidoras, ajustesFeriados]);

  // Atraso geral da Equatorial, por mês e com todos os analistas: quando muitas faturas
  // atrasam juntas, o problema é a distribuidora, não cada UC.
  const mesesComAtrasoGeral = useMemo(
    () => atrasoPorMes(
      cobrancasMapeadas.map(item => ({ mes: item.expectedMonth, previsao: item.previsaoFatura, temFatura: item.hasFatura })),
      chaveData(new Date()),
    ).filter(m => m.geral),
    [cobrancasMapeadas],
  );

  // Meses montados com a planilha de outro mês, porque a do mês anterior ainda não subiu
  // (src/lib/planilhaRateioVigente.ts). Contagem de UCs sem filtro de tela.
  const mesesComPlanilhaRepetida = useMemo(() => {
    const porMes = new Map<string, { planilha: string; ucs: number }>();
    for (const p of pendentes) {
      if (!p.planilhaRepetida) continue;
      const atual = porMes.get(p.expectedMonth) ?? { planilha: p.planilhaRepetida, ucs: 0 };
      atual.ucs += 1;
      porMes.set(p.expectedMonth, atual);
    }
    return ordenarMeses([...porMes.keys()]).map(mes => ({ mes, esperada: mesAnterior(mes), ...porMes.get(mes)! }));
  }, [pendentes]);

  const displayItems = useMemo(() => {
    // 2. Aplica os filtros ativados na tela (Mês, Busca, e agora Analista!)
    return cobrancasMapeadas.filter(item => {
      if (selectedMonthFilter !== 'Todos' && item.expectedMonth !== selectedMonthFilter) return false;
      if (selectedAnalystFilter !== 'Todos' && item.responsavel_id !== selectedAnalystFilter) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (item.nome || '').toLowerCase().includes(term) ||
          (item.uc || '').includes(term) ||
          (item.outro_numero_uc || '').includes(term) ||
          (item.ucData?.nome_cliente || '').toLowerCase().includes(term) ||
          (item.ucData?.titular_fatura || '').toLowerCase().includes(term);
      }
      return true;
    }).sort((a, b) => {
      const weight = { critical: 3, warning: 2, sem_previsao: 2, normal: 1 };
      const wA = weight[a.status as keyof typeof weight] || 1;
      const wB = weight[b.status as keyof typeof weight] || 1;
      if (wA !== wB) return wB - wA;

      if (a.criticoJanelaIso && b.criticoJanelaIso) {
        return a.criticoJanelaIso.getTime() - b.criticoJanelaIso.getTime();
      }
      return 0;
    });
  }, [cobrancasMapeadas, searchTerm, selectedMonthFilter, selectedAnalystFilter]);

  const totalPendingInModal = useMemo(() => {
    return displayItems.filter(i => !i.isCalculada).length;
  }, [displayItems]);

  // --- KANBAN COLUMNS DISTRIBUTION (Max 40 per day, with forward pulling) ---
  const kanbanData = useMemo(() => {
    // 1. Determinar a data de hoje do sistema local
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    // 2. Capacidade máxima: 40 itens por analista
    const maxPerDay = analysts.length > 0 ? analysts.length * 40 : 40;

    // 3. Lista de datas a renderizar
    const renderDates: Date[] = [];
    if (viewMode === 'semana') {
      for (let i = 0; i < 5; i++) {
        const d = new Date(currentWeekStart);
        d.setDate(d.getDate() + i);
        renderDates.push(d);
      }
    } else {
      renderDates.push(selectedDay);
    }

    const toDateStr = (d: Date | null) => d ? d.toISOString().split('T')[0] : '';

    // Função auxiliar para pegar a data em que o item foi finalizado
    const getDoneDateStr = (item: any) => {
      const f = item.fatura;
      if (!f) return toDateStr(item.targetDateIso);

      // Se tiver data explícita de ação, usar essa data
      if (f.pular_calculo_em) return new Date(f.pular_calculo_em).toISOString().split('T')[0];
      if (f.emitido_em) return new Date(f.emitido_em).toISOString().split('T')[0];
      if (f.enviado_em) return new Date(f.enviado_em).toISOString().split('T')[0];
      if (f.updated_at) return new Date(f.updated_at).toISOString().split('T')[0];
      if (f.created_at) return new Date(f.created_at).toISOString().split('T')[0];

      return toDateStr(item.targetDateIso);
    };

    // 4. Agrupar itens concluídos pela data de conclusão e itens pendentes pela data prevista
    const completedByDate: Record<string, any[]> = {};
    const pendingByDate: Record<string, any[]> = {};

    displayItems.forEach(item => {
      const f = item.fatura;

      // A. Se foi reagendado, registra uma atividade FEITA virtual na data do reagendamento
      if (f?.reagendamento_em) {
        const reagStr = new Date(f.reagendamento_em).toISOString().split('T')[0];
        if (reagStr) {
          if (!completedByDate[reagStr]) completedByDate[reagStr] = [];
          completedByDate[reagStr].push({
            ...item,
            isCalculada: true,
            tipoAtividadeFeita: 'reagendado'
          });
        }
      }

      // B. Processa o destino original do item (como concluído ou pendente)
      if (item.isCalculada) {
        const doneStr = getDoneDateStr(item);
        if (doneStr) {
          if (!completedByDate[doneStr]) completedByDate[doneStr] = [];

          // Determina se a atividade concluída foi calculada ou pulada
          const tipo = f?.pular_calculo_em ? 'pulado' : 'calculado';

          completedByDate[doneStr].push({
            ...item,
            tipoAtividadeFeita: tipo
          });
        }
      } else {
        const schedStr = toDateStr(item.targetDateIso);
        if (schedStr) {
          if (!pendingByDate[schedStr]) pendingByDate[schedStr] = [];
          pendingByDate[schedStr].push(item);
        } else {
          // Sem data prevista: entra direto no backlog para aparecer na primeira coluna disponível
          if (!pendingByDate['__sem_data__']) pendingByDate['__sem_data__'] = [];
          pendingByDate['__sem_data__'].push(item);
        }
      }
    });

    // 5. Coletar todos os pendentes antigos (agendados antes do início da renderização) no backlog
    const sortedPendingDates = Object.keys(pendingByDate).sort();
    const startRenderDateStr = renderDates.length > 0 ? toDateStr(renderDates[0]) : '';

    const urgencyWeight = { critical: 3, warning: 2, sem_previsao: 2, normal: 1 };
    const sortByUrgency = (arr: any[]) => arr.sort((a, b) => {
      const wA = urgencyWeight[a.status as keyof typeof urgencyWeight] || 1;
      const wB = urgencyWeight[b.status as keyof typeof urgencyWeight] || 1;
      if (wA !== wB) return wB - wA;
      if (a.criticoJanelaIso && b.criticoJanelaIso) {
        return a.criticoJanelaIso.getTime() - b.criticoJanelaIso.getTime();
      }
      return 0;
    });

    const backlogQueue: any[] = [...(pendingByDate['__sem_data__'] || [])];
    sortedPendingDates.forEach(dateStr => {
      if (dateStr < startRenderDateStr) {
        backlogQueue.push(...pendingByDate[dateStr]);
      }
    });

    // Críticos de qualquer dia da janela entram no backlog imediatamente,
    // ordenados pela criticoJanelaIso mais antiga (maior urgência real).
    // São removidos do pendingByDate para não serem adicionados novamente no loop.
    sortedPendingDates.forEach(dateStr => {
      if (dateStr >= startRenderDateStr) {
        const criticals = (pendingByDate[dateStr] || []).filter((i: any) => i.status === 'critical' && !i.hasManualReschedule);
        if (criticals.length > 0) {
          backlogQueue.push(...criticals);
          pendingByDate[dateStr] = pendingByDate[dateStr].filter((i: any) => !(i.status === 'critical' && !i.hasManualReschedule));
        }
      }
    });

    sortByUrgency(backlogQueue);

    const finalDistribution: Record<string, any[]> = {};

    // Processar cada dia cronologicamente
    renderDates.forEach(dateObj => {
      const dateStr = toDateStr(dateObj);
      finalDistribution[dateStr] = [];

      // A. Adicionar os pendentes previstos especificamente para esse dia à fila de backlog
      // (críticos já foram extraídos acima, então só entram warning/normal aqui)
      if (pendingByDate[dateStr]) {
        backlogQueue.push(...pendingByDate[dateStr]);
        sortByUrgency(backlogQueue);
      }

      // B. Se for Hoje ou Futuro, preenchemos a coluna primeiro com itens do backlog até atingir maxPerDay
      // Se o dia renderizado for no Passado, NÃO puxamos pendentes (pois eles já rolaram para Hoje/Futuro)
      if (dateStr >= todayStr) {
        let slotsTaken = 0;
        while (slotsTaken < maxPerDay && backlogQueue.length > 0) {
          finalDistribution[dateStr].push(backlogQueue.shift());
          slotsTaken++;
        }
      }

      // C. Adicionar itens CONCLUÍDOS por ÚLTIMO (estático, empurra para o final da lista)
      const completedToday = completedByDate[dateStr] || [];
      finalDistribution[dateStr].push(...completedToday);
    });

    // 6. Atribuição já realizada antecipadamente no displayItems para consistência total de contagem e filtragem

    return { renderDates, finalDistribution, maxPerDay };

  }, [displayItems, currentWeekStart, selectedDay, viewMode, analysts]);

  // Upload Handler
  const handleUploadClick = (uc: string) => {
    setUploadingForUc(uc);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    } else {
      setUploadingForUc(null);
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const uc = uploadingForUc;

    if (!file || !uc) {
      setUploadingForUc(null);
      return;
    }

    if (file.type !== 'application/pdf') {
      addToast('Por favor, selecione um arquivo PDF válido.', 'error');
      setUploadingForUc(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (!webhookUrl) {
      addToast('URL do Webhook não configurada.', 'error');
      setUploadingForUc(null);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      // Opcionalmente enviar metadata uc_codigo se o n8n aceitar
      formData.append('uc_codigo', uc);

      const response = await fetch(webhookUrl, {
        method: 'POST',
        body: formData,
      });

      const responseData = await response.json().catch(() => ({})) as Record<string, any>;

      if (!response.ok || responseData?.success === false) {
        throw new Error(responseData?.error || 'Falha ao enviar arquivo para o sistema.');
      }

      setUploadSuccessUc(uc);
      addToast(`Fatura importada e enviada para a fila de processamento!`, 'success');

      // A fatura nasce no n8n, sem id conhecido aqui: recarrega pela UC. O tempo
      // real cobre o caso de o processamento demorar mais que isso.
      const ucsDoUpload = [uc, cobrancasMapeadas.find((i: any) => i.uc_identificadora === uc)?.outro_numero_uc]
        .filter(Boolean) as string[];
      setTimeout(() => {
        setUploadSuccessUc(null);
        onActionComplete({ ucCodigos: ucsDoUpload });
      }, 4000);
      setTimeout(() => onActionCompleteRef.current({ ucCodigos: ucsDoUpload }), 20000);
    } catch (error: any) {
      console.error('Upload error:', error);
      addToast(error.message || 'Erro inesperado ao enviar arquivo.', 'error');
    } finally {
      setUploadingForUc(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Equatorial Retry Handler
  const handleRetryEquatorial = async (queueId: string, item: any) => {
    try {
      // Re-busca os dados atuais da UC para garantir credenciais corretas
      // (usuário pode ter corrigido CPF/data nascimento após a falha)
      const ucBusca = item.outro_numero_uc || item.uc;
      const { data: ucAtual } = await supabase
        .from('unidades_consumidoras')
        .select('cpf_cnpj_titular, data_nascimento_titular, nome_cliente')
        .or(`numero_uc.eq.${ucBusca},outro_numero_uc.eq.${ucBusca}`)
        .limit(1)
        .maybeSingle();

      const updatePayload: Record<string, any> = {
        status: 'pendente',
        tentativas: 0,
        error_details: null,
        updated_at: new Date().toISOString(),
        prioridade: 1,
      };

      if (ucAtual?.cpf_cnpj_titular) {
        const cpfCnpj = ucAtual.cpf_cnpj_titular.replace(/\D/g, '');
        updatePayload['cpf_cnpj'] = cpfCnpj;

        if (cpfCnpj.length === 11 && ucAtual.data_nascimento_titular) {
          const raw = String(ucAtual.data_nascimento_titular);
          let dataNasc = raw;
          if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
            const [y, m, d] = raw.split('T')[0].split('-');
            dataNasc = `${d}/${m}/${y}`;
          }
          updatePayload['data_nascimento'] = dataNasc;
        } else if (cpfCnpj.length === 14) {
          updatePayload['data_nascimento'] = null;
        }
      }

      if (ucAtual?.nome_cliente) {
        updatePayload['nome_cliente'] = ucAtual.nome_cliente;
      }

      const { error: retryError } = await supabase
        .from('fatura_download_queue')
        .update(updatePayload)
        .eq('id', queueId);

      if (retryError) throw retryError;

      // Registra no relatório que a tentativa foi reiniciada manualmente —
      // historico_execucoes não é limpo pelo update acima, só acumulado.
      supabase.rpc('fdq_registrar_execucao', {
        p_queue_id: queueId,
        p_entrada: {
          timestamp: new Date().toISOString(),
          tentativa: 0,
          status: 'reiniciado',
          mensagem: `Tarefa reiniciada manualmente por ${profile?.full_name || user?.email || 'usuário'}.`,
        },
      }).then(({ error }) => {
        if (error) console.error('[Equatorial] falha ao registrar retry no histórico:', error);
        refreshQueueRef.current();
      });

      setQueueStatusByUc(prev => {
        const next = new Map(prev);
        const mesReferencia = item.fatura?.mes_referencia || item.expectedMonth;
        const _retryUcKey = item.fatura?.uc_codigo || item.outro_numero_uc || item.uc;
        const chave = `${_retryUcKey}:${mesReferencia}`;
        next.set(chave, { ...next.get(chave), status: 'pendente' });
        return next;
      });
      addToast('Tarefa reiniciada com os dados atualizados da UC.', 'success');
    } catch {
      addToast('Erro ao reiniciar a tarefa.', 'error');
    }
  };

  // Equatorial Download Handler
  // modoBusca 'ultima_disponivel': usado nos cards "Sem Histórico" — o robô ignora
  // o mês e baixa a fatura mais recente disponível, usada só pra semear
  // unidades_consumidoras.hist_manual_* (nunca cria uma fatura real).
  const handleEnfileirarEquatorial = async (item: any, modoBusca: 'mes_especifico' | 'ultima_disponivel' = 'mes_especifico') => {
    setEnqueuingForUc(item.uc_identificadora);
    try {
      const ucData = item.ucData;
      const cpfCnpjRaw = ucData?.cpf_cnpj_titular || item.cpf_cnpj || '';
      const cpfCnpj = cpfCnpjRaw.replace(/\D/g, '');
      const dataNascRaw: string | null = ucData?.data_nascimento_titular ?? null;

      // Converte DATE ISO (yyyy-mm-dd) para dd/mm/yyyy que o robô espera
      let dataNasc: string | null = null;
      if (dataNascRaw) {
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dataNascRaw)) {
          dataNasc = dataNascRaw;
        } else if (/^\d{4}-\d{2}-\d{2}/.test(dataNascRaw)) {
          const [y, m, d] = dataNascRaw.split('T')[0].split('-');
          dataNasc = `${d}/${m}/${y}`;
        } else {
          dataNasc = dataNascRaw;
        }
      }

      const mesReferencia = item.fatura?.mes_referencia || item.expectedMonth;
      let faturaId: number | null = item.fatura?.id ?? null;

      // Se não tem fatura, verifica antes se já existe uma pelo número alternativo da UC
      if (!faturaId) {
        const ucCodigos = [
          item.uc_identificadora,
          item.outro_numero_uc,
          item.ucData?.outro_numero_uc,
        ].filter(Boolean).map((uc: string) => String(uc).replace(/\D/g, '')).filter(Boolean);

        if (ucCodigos.length > 0) {
          const { data: faturasExistentes } = await supabase
            .from('faturas')
            .select('id, uc_codigo, status_fatura, classificacao')
            .in('uc_codigo', ucCodigos)
            .eq('mes_referencia', mesReferencia)
            .not('status_fatura', 'in', '(Excluído,Cancelado,Aguardando Upload)')
            .neq('classificacao', 'AVULSO')
            .limit(1);

          if (faturasExistentes && faturasExistentes.length > 0) {
            const fat = faturasExistentes[0];
            addToast(
              `Já existe fatura ativa para UC ${fat.uc_codigo} em ${mesReferencia} (${fat.status_fatura}). Não é necessário baixar novamente.`,
              'warning'
            );
            return;
          }
        }
      }

      // Cria um placeholder para o robô atualizar depois
      if (!faturaId) {
        const ucCodigo = item.outro_numero_uc || item.uc;
        const { data: novaFatura, error: errFatura } = await supabase
          .from('faturas')
          .insert({
            uc_codigo: ucCodigo,
            cliente_nome: item.nome,
            cliente_cpf_cnpj: cpfCnpj,
            mes_referencia: mesReferencia,
            status_fatura: 'Aguardando Upload',
            responsavel_cobranca_id: item.responsavel_id || null,
            valor_fatura_distribuidora: 0,
            valor_consumo_nao_comp: 0,
            valor_adc_bandeira: 0,
            valor_parc_injet_s_desc: 0,
            valor_restituicao_imposto: 0,
          })
          .select('id')
          .single();

        if (errFatura || !novaFatura) {
          console.error('[Equatorial] fatura INSERT error:', errFatura);
          throw new Error(`Erro ao criar registro de fatura: ${errFatura?.message || 'verifique o console'}`);
        }
        faturaId = novaFatura.id;
      }

      const { error: errQueue } = await supabase
        .from('fatura_download_queue')
        .insert({
          fatura_id: faturaId,
          uc_codigo: item.fatura?.uc_codigo || item.outro_numero_uc || item.uc,
          mes_referencia: mesReferencia,
          cpf_cnpj: cpfCnpj,
          nome_cliente: item.nome,
          data_nascimento: cpfCnpj.length === 11 ? dataNasc : null,
          tipo_fatura: 'definitiva',
          prioridade: 1,
          modo_busca: modoBusca,
        });

      if (errQueue) {
        // Código 23505 = violação de unique constraint → entrada já existe na fila
        // Nesse caso escalamos a prioridade em vez de falhar
        if (errQueue.code === '23505' || errQueue.message?.includes('duplicate key value')) {
          const ucCodigo = item.fatura?.uc_codigo || item.outro_numero_uc || item.uc;
          const { error: prioError } = await supabase
            .from('fatura_download_queue')
            .update({ prioridade: 1, updated_at: new Date().toISOString() })
            .eq('uc_codigo', ucCodigo)
            .eq('mes_referencia', mesReferencia)
            .in('status', ['pendente', 'processando']);

          if (prioError) throw new Error('Erro ao elevar prioridade: ' + prioError.message);

          addToast('Fatura já estava na fila — prioridade elevada para urgente!', 'success');
          const _escUcKey = item.fatura?.uc_codigo || item.outro_numero_uc || item.uc;
          setQueueStatusByUc(prev => {
            const next = new Map(prev);
            next.set(`${_escUcKey}:${mesReferencia}`, { status: 'pendente', prioridade: 1 });
            return next;
          });
          return;
        }
        throw new Error('Erro ao adicionar na fila: ' + errQueue.message);
      }

      addToast('Adicionado na fila com prioridade! O robô processará em breve.', 'success');
      // Atualiza o mapa com a mesma chave usada no INSERT (outro_numero_uc tem precedência)
      const _queueUcKey = item.fatura?.uc_codigo || item.outro_numero_uc || item.uc;
      setQueueStatusByUc(prev => {
        const next = new Map(prev);
        next.set(`${_queueUcKey}:${mesReferencia}`, { status: 'pendente', prioridade: 1 });
        return next;
      });
      onActionComplete(faturaId ? { faturaIds: [faturaId] } : undefined);
      refreshQueueRef.current();
    } catch (error: any) {
      addToast(error.message || 'Erro ao enfileirar fatura.', 'error');
    } finally {
      setEnqueuingForUc(null);
    }
  };

  // Skip Handler
  const handleSkipCalculation = async () => {
    if (!skippingItem) return;
    const camposMotivo = estadoPulo.campos;
    if (!camposMotivo) {
      addToast(estadoPulo.erro || 'Selecione o motivo.', 'warning');
      return;
    }

    setLoading(true);
    try {
      const { fatura, uc, outro_numero_uc, nome, expectedMonth, cpf_cnpj, responsavel_id } = skippingItem;
      const userName = user?.user_metadata?.full_name || user?.email || 'Usuário Desconhecido';

      const aplicarSkipNaFatura = async (faturaId: number) => {
        // O item da lista não traz o número do boleto: sem ele não dá para saber se
        // há boleto vivo, e o valor registrado no banco só pode ser limpo sem boleto (#6246).
        const { data: atual, error: erroLeitura } = await supabase.from('faturas')
          .select('num_cobranca_boleto').eq('id', faturaId).maybeSingle();
        if (erroLeitura) throw erroLeitura;
        const { error } = await supabase.from('faturas').update({
          status_fatura: 'Com boleto emitido / A enviar',
          pular_calculo_por_nome: userName,
          pular_calculo_em: new Date().toISOString(),
          ...camposMotivo,
          // Mesmo padrão do "Pular Cálculo" em FaturaValidationModal: sem isso, um
          // valor_boleto_vigor herdado de importação/duplicata da mesma UC
          // permanece e a fatura vira "Vencido" fantasma quando a data passa. Ver
          // src/lib/faturaSemInjecao.ts.
          ...camposZeradosSemInjecao(atual),
          responsavel_cobranca_id: responsavel_id
        }).eq('id', faturaId);
        if (error) throw error;

        await logAction(user?.id || 'SYSTEM', 'SKIP_CALCULATION', {
          fatura_id: faturaId,
          motivo: camposMotivo.motivo_pular_calculo,
          motivo_codigo: camposMotivo.motivo_pular_calculo_codigo,
          contexto: 'Janela de Cobrança'
        });
        logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'pular_calculo', origem: 'acao' });
      };

      let faturaAlterada: number | null = null;
      if (fatura) {
        await aplicarSkipNaFatura(fatura.id);
        faturaAlterada = fatura.id;
      } else {
        // Revalida no banco antes de criar: entre a lista de pendentes ter sido montada
        // e o clique em confirmar, uma fatura real pode ter chegado (robô/importação)
        // para essa mesma UC+mês. Sem essa checagem, o INSERT abaixo criaria uma segunda
        // linha duplicada, e a UC voltaria a aparecer como pendente dependendo de qual
        // das duas linhas o sistema escolhesse exibir.
        const ucCandidates = Array.from(new Set([uc, outro_numero_uc].filter(Boolean)));
        const { data: existentes, error: errCheck } = await supabase
          .from('faturas')
          .select('id, status_fatura')
          .eq('mes_referencia', expectedMonth)
          .in('uc_codigo', ucCandidates)
          .neq('status_fatura', 'Excluído')
          .order('id', { ascending: true });
        if (errCheck) throw errCheck;

        const faturaJaExistente = existentes?.find(f => f.status_fatura !== 'Aguardando Upload') ?? existentes?.[0] ?? null;

        if (faturaJaExistente) {
          await aplicarSkipNaFatura(faturaJaExistente.id);
          faturaAlterada = faturaJaExistente.id;
        } else {
          const { data, error } = await supabase.from('faturas').insert({
            uc_codigo: outro_numero_uc || uc,
            cliente_nome: nome,
            cliente_cpf_cnpj: cpf_cnpj,
            mes_referencia: expectedMonth,
            status_fatura: 'Com boleto emitido / A enviar',
            pular_calculo_por_nome: userName,
            pular_calculo_em: new Date().toISOString(),
            ...camposMotivo,
            valor_fatura_distribuidora: 0,
            ...CAMPOS_ZERADOS_SEM_INJECAO,
            created_at: new Date().toISOString(),
            responsavel_cobranca_id: responsavel_id
          }).select();

          if (error) throw error;

          if (data && data[0]) {
            faturaAlterada = data[0].id;
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'criar_fatura_sem_calculo', origem: 'acao' });
            await logAction(user?.id || 'SYSTEM', 'CREATE_FATURA_SKIP_CALCULATION', {
              fatura_id: data[0].id,
              uc: outro_numero_uc || uc,
              motivo: camposMotivo.motivo_pular_calculo,
              motivo_codigo: camposMotivo.motivo_pular_calculo_codigo,
              contexto: 'Janela de Cobrança'
            });
          }
        }
      }

      addToast('Ação registrada com sucesso.', 'success');
      setSkippingItem(null);
      limparMotivoSkip();
      onActionComplete(faturaAlterada ? { faturaIds: [faturaAlterada] } : undefined);
    } catch (err: any) {
      console.error(err);
      addToast(`Erro ao registrar ação: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferItem || !transferTargetId) return;
    setTransferring(true);
    try {
      const { fatura, uc, outro_numero_uc, nome, expectedMonth, cpf_cnpj } = transferItem;

      let faturaAlterada: number;
      if (fatura && fatura.id) {
        const { error } = await supabase
          .from('faturas')
          .update({ responsavel_cobranca_id: transferTargetId })
          .eq('id', fatura.id);
        if (error) throw error;
        faturaAlterada = fatura.id;
      } else {
        const { data: esboco, error } = await supabase.from('faturas').insert({
          uc_codigo: outro_numero_uc || uc,
          cliente_nome: nome,
          cliente_cpf_cnpj: cpf_cnpj,
          mes_referencia: expectedMonth,
          status_fatura: 'Aguardando Upload',
          classificacao: 'NORMAL',
          responsavel_cobranca_id: transferTargetId,
          created_at: new Date().toISOString(),
        }).select('id').single();
        if (error) throw error;
        faturaAlterada = esboco.id;
      }

      logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'transferir_cobranca', origem: 'acao' });
      await logAction(user?.id || 'SYSTEM', 'TRANSFER_COBRANCA', {
        uc: outro_numero_uc || uc,
        mes_referencia: expectedMonth,
        de: transferItem.responsavel_id,
        para: transferTargetId,
        contexto: 'Janela de Cobrança'
      });

      addToast('Cobrança transferida com sucesso', 'success');
      setTransferItem(null);
      setTransferTargetId('');
      onActionComplete({ faturaIds: [faturaAlterada] });
    } catch (err: any) {
      addToast(err.message || 'Erro ao transferir cobrança', 'error');
    } finally {
      setTransferring(false);
    }
  };

  const handleExportExcel = () => {
    try {
      if (displayItems.length === 0) {
        addToast('Nenhum dado para exportar.', 'warning');
        return;
      }

      const headers = [
        'Unidade Consumidora (UC)',
        'Outro Número UC',
        'Cliente / Razão Social',
        'Mês de Referência',
        'Rateio',
        'Status no Rateio',
        'Início da Janela',
        'Fim da Janela',
        'Limite Crítico',
        'Status da Janela',
        'Já Possui Fatura?',
        'Diferencial / Tipo'
      ];

      const rows = displayItems.map(item => {
        const formatLocalDate = (dateObj: Date | null) => {
          if (!dateObj || isNaN(dateObj.getTime())) return '—';
          return dateObj.toLocaleDateString('pt-BR');
        };

        const statusLabel =
          item.status === 'critical' ? 'CRÍTICO' :
            item.status === 'warning' ? 'ATENÇÃO' : 
            item.status === 'sem_previsao' ? 'SEM PREVISÃO' : 'NORMAL';

        return [
          item.uc || '—',
          item.outro_numero_uc || '—',
          item.nome || '—',
          item.expectedMonth || '—',
          item.rateioNome || '—',
          classificarStatusRateio(item.statusRateio)?.rotulo ?? '—',
          formatLocalDate(item.inicioJanelaIso),
          formatLocalDate(item.fimJanelaIso),
          formatLocalDate(item.criticoJanelaIso),
          statusLabel,
          item.hasFatura ? 'Sim' : 'Não (Aguardando Upload)',
          item.isPredictive ? 'Janela Prevista' : 'Janela Real'
        ];
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

      const wscols = headers.map((h, i) => {
        let maxLen = h.length;
        rows.forEach(row => {
          const cellVal = String(row[i] || '');
          if (cellVal.length > maxLen) maxLen = cellVal.length;
        });
        return { wch: maxLen + 3 };
      });
      ws['!cols'] = wscols;

      XLSX.utils.book_append_sheet(wb, ws, 'Pendentes_Faturamento');

      const now = new Date();
      const datePart = now.toLocaleDateString('pt-BR').replace(/\//g, '-');
      const timePart = now.toLocaleTimeString('pt-BR').replace(/:/g, '');
      XLSX.writeFile(wb, `Janela_Faturamento_${datePart}_${timePart}.xlsx`);
      addToast('Dados exportados para Excel com sucesso!', 'success');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      addToast('Erro ao exportar dados para Excel.', 'error');
    }
  };

  // Botão compacto que fica antes do botão "Equatorial" — abre o relatório
  // de tentativas do robô (historico_execucoes) quando existe alguma.
  const BotaoHistoricoRobo = ({ qEntry }: { qEntry: any }) => {
    const historico = qEntry?.historico_execucoes;
    if (!Array.isArray(historico) || historico.length === 0) return null;
    return (
      <button
        onClick={() => setHistoricoRoboItem(qEntry)}
        title="Ver relatório de tentativas do robô"
        className="px-1.5 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-300 rounded-lg transition-colors shrink-0"
      >
        <History size={10} />
      </button>
    );
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4"
      style={{ zIndex: 8000 }}
      onMouseDown={e => { backdropMouseDownRef.current = e.target === e.currentTarget; }}
      onClick={() => { if (backdropMouseDownRef.current) onClose(); backdropMouseDownRef.current = false; }}
    >
      {/* Hidden file input for uploads */}
      <input
        type="file"
        ref={fileInputRef}
        accept="application/pdf"
        onChange={handleFileSelected}
        className="hidden"
      />

      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-[95vw] max-w-[95vw] h-[95vh] max-h-[95vh] flex flex-col overflow-hidden border dark:border-gray-700 animate-in fade-in zoom-in duration-200 relative" onClick={e => e.stopPropagation()}>

        {/* Close Button Top Right */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors z-10"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="pt-8 px-6 pb-6 border-b dark:border-gray-700 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50/50 dark:bg-gray-800/50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#5F6C37]/10 text-[#5F6C37] rounded-xl flex items-center justify-center">
              <Calculator size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Janela de Faturamento / Cobrança</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Gestão diária de emissões e rateios
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* View Mode Toggle */}
            <div className="flex bg-gray-200 dark:bg-gray-700 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('semana')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'semana' ? 'bg-white dark:bg-gray-800 text-[#5F6C37] shadow' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}
              >
                Semana
              </button>
              <button
                onClick={() => setViewMode('dia')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'dia' ? 'bg-white dark:bg-gray-800 text-[#5F6C37] shadow' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}
              >
                Dia
              </button>
              <button
                onClick={() => setViewMode('lista')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'lista' ? 'bg-white dark:bg-gray-800 text-[#5F6C37] shadow' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}
              >
                Lista
              </button>
            </div>

            {/* Week Navigation */}
            {viewMode === 'semana' && (
              <div className="flex items-center gap-2 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setCurrentWeekStart(new Date(currentWeekStart.setDate(currentWeekStart.getDate() - 7)))}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-600 dark:text-gray-300"
                >
                  <ChevronRight size={18} className="rotate-180" />
                </button>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200 min-w-[120px] text-center">
                  {currentWeekStart.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} - {new Date(new Date(currentWeekStart).setDate(currentWeekStart.getDate() + 4)).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                </span>
                <button
                  onClick={() => setCurrentWeekStart(new Date(currentWeekStart.setDate(currentWeekStart.getDate() + 7)))}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-600 dark:text-gray-300"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}

            {/* Day Selector (If viewMode is Day) */}
            {viewMode === 'dia' && (
              <div className="flex items-center gap-2 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-1">
                <input
                  type="date"
                  value={selectedDay.toISOString().split('T')[0]}
                  onChange={(e) => setSelectedDay(new Date(e.target.value + 'T12:00:00'))}
                  className="bg-transparent border-none outline-none text-sm font-medium text-gray-700 dark:text-gray-200 px-2"
                />
              </div>
            )}
          </div>
        </div>

        {mesesComAtrasoGeral.length > 0 ? (
          <div role="status" className="mx-4 sm:mx-6 mt-4 flex items-start gap-3 rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-amber-900 dark:text-amber-200 shrink-0">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div className="text-sm min-w-0">
              <p className="font-bold">Atraso geral da Equatorial</p>
              {mesesComAtrasoGeral.map(m => (
                <p key={m.mes}>
                  {m.mes}: {m.atrasadas} de {m.comPrazoVencido} UCs ({Math.round(m.fracao * 100)}%) ainda sem fatura depois do 9º dia útil da leitura.
                </p>
              ))}
              <p className="text-xs mt-1 text-amber-800/80 dark:text-amber-200/80">
                Com tantas ao mesmo tempo, o atraso é da distribuidora, não de cada UC. Em mês normal, só 1% a 7% passam desse prazo.
              </p>
            </div>
          </div>
        ) : null}

        {mesesComPlanilhaRepetida.length > 0 ? (
          <div role="status" className="mx-4 sm:mx-6 mt-4 flex items-start gap-3 rounded-xl border border-sky-200 dark:border-sky-900/50 bg-sky-50 dark:bg-sky-900/20 px-4 py-3 text-sky-900 dark:text-sky-200 shrink-0">
            <Calendar size={18} className="shrink-0 mt-0.5" />
            <div className="text-sm min-w-0">
              <p className="font-bold">Planilha de rateio repetida</p>
              {mesesComPlanilhaRepetida.map(m => (
                <p key={m.mes}>
                  {m.mes}: a planilha de {m.esperada ?? 'rateio do mês anterior'} ainda não foi carregada. A janela usa a de {m.planilha} ({m.ucs.toLocaleString('pt-BR')} UCs).
                </p>
              ))}
              <p className="text-xs mt-1 text-sky-800/80 dark:text-sky-200/80">
                Quando a planilha certa subir, a janela troca sozinha. UC que entrou no rateio depois de {mesesComPlanilhaRepetida[0].planilha} só aparece com ela.
              </p>
            </div>
          </div>
        ) : null}

        {viewMode === 'lista' ? (
          <JanelaFaturamentoLista
            itens={cobrancasMapeadas}
            analysts={analysts}
            responsavel={selectedAnalystFilter === 'Todos' ? '' : selectedAnalystFilter}
            onResponsavelChange={(id) => setSelectedAnalystFilter(id || 'Todos')}
            filaRobo={queueStatusByUc}
            enqueuingForUc={enqueuingForUc}
            uploadingForUc={uploadingForUc}
            uploadSuccessUc={uploadSuccessUc}
            podeTransferir={canTransferItem}
            onCalcular={(item) => item.fatura?.id && onOpenValidation(item.fatura.id)}
            onVisualizar={(item) => item.fatura?.id && onOpenValidation(item.fatura.id, true)}
            onReagendar={(item) => {
              setRescheduleItem(item);
              setNewScheduleDate(chaveData(item.targetDateIso) || chaveData(new Date()));
            }}
            onVerReagendamento={setViewingReschedule}
            onTransferir={(item) => { setTransferItem(item); setTransferTargetId(''); }}
            onPular={setSkippingItem}
            onEquatorial={handleEnfileirarEquatorial}
            onRetentarRobo={handleRetryEquatorial}
            onVerHistoricoRobo={setHistoricoRoboItem}
            onAtualizarDadosTitular={(item, tentativa) => setCorrigindoTitular({ item, tentativa })}
            onUpload={handleUploadClick}
            onExportado={(ok, msg) => addToast(msg, ok ? 'success' : 'warning')}
          />
        ) : (<>
        {/* Filters */}
        <div className="p-4 border-b dark:border-gray-700 flex flex-col md:flex-row items-center gap-4 bg-white dark:bg-gray-800 shrink-0">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Buscar por UC ou Nome..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-gray-700 border-none rounded-xl focus:ring-2 focus:ring-[#5F6C37] outline-none text-gray-800 dark:text-gray-200 text-sm"
            />
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <span className="text-sm font-bold text-red-600 bg-red-50 dark:bg-red-900/20 px-3 py-1.5 rounded-lg whitespace-nowrap flex items-center gap-1.5 border border-red-100 dark:border-red-900/30">
              <Clock size={16} />
              {totalPendingInModal} {totalPendingInModal === 1 ? 'cobrança pendente' : 'cobranças pendentes'}
            </span>
            <span className="text-sm font-semibold text-gray-500 hidden sm:block">Responsável:</span>
            <select
              value={selectedAnalystFilter}
              onChange={(e) => setSelectedAnalystFilter(e.target.value)}
              className="bg-gray-100 dark:bg-gray-700 text-sm border-none rounded-xl px-4 py-2 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-[#5F6C37]"
            >
              <option value="Todos">Todos os Analistas</option>
              {analysts.map(analyst => (
                <option key={analyst.id} value={analyst.id}>{analyst.full_name || analyst.email}</option>
              ))}
            </select>

            <span className="text-sm font-semibold text-gray-500 hidden sm:block">Mês Referência:</span>
            <select
              value={selectedMonthFilter}
              onChange={(e) => setSelectedMonthFilter(e.target.value)}
              className="bg-gray-100 dark:bg-gray-700 text-sm border-none rounded-xl px-4 py-2 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-[#5F6C37]"
            >
              <option value="Todos">Todos os Meses</option>
              {uniqueMonths.map(month => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>
            <button
              onClick={handleExportExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-bold transition-all shadow-lg shadow-green-600/10 shrink-0"
            >
              <Download size={16} />
              <span>Exportar Excel</span>
            </button>
          </div>
        </div>

        {/* Table Container */}
        <div className={`flex-1 p-4 bg-gray-50/30 dark:bg-gray-900/10 ${viewMode === 'semana' ? 'overflow-x-auto overflow-y-hidden flex flex-col min-h-0' : 'overflow-auto custom-scrollbar'}`}>
          {displayItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <CheckCircle2 size={48} className="mb-4 text-[#5F6C37] opacity-20" />
              <p className="text-lg font-medium">Tudo limpo!</p>
              <p className="text-sm">Nenhuma cobrança encontrada para este filtro.</p>
            </div>
          ) : (
            <>
              {viewMode === 'dia' && (
                <style dangerouslySetInnerHTML={{
                  __html: `
                  .dia-view-container .text-\\[10px\\] { font-size: 0.875rem !important; }
                  .dia-view-container .text-\\[9px\\] { font-size: 0.8rem !important; }
                  .dia-view-container .text-\\[8px\\] { font-size: 0.75rem !important; }
                  .dia-view-container .text-\\[11px\\] { font-size: 0.875rem !important; }
                  .dia-view-container .text-xs { font-size: 0.875rem !important; }
                  .dia-view-container .text-sm { font-size: 1.1rem !important; }
                  .dia-view-container svg { transform: scale(1.15) !important; transform-origin: center !important; }
                  .dia-view-container .p-4 { padding: 1.75rem !important; }
                  .dia-view-container .gap-3 { gap: 1.25rem !important; }
                `}} />
              )}
              <div className={`grid gap-6 ${viewMode === 'semana' ? 'grid-cols-1 lg:grid-cols-5 min-w-max lg:min-w-0 flex-1 min-h-0 items-stretch' : 'grid-cols-1 max-w-6xl mx-auto w-full px-4 dia-view-container'}`}>
                {kanbanData.renderDates.map(date => {
                  const dateStr = date.toISOString().split('T')[0];
                  const items = kanbanData.finalDistribution[dateStr] || [];

                  return (
                    <div key={dateStr} className={`flex flex-col gap-3 ${viewMode === 'semana' ? 'min-w-[300px] h-full min-h-0' : ''}`}>
                      {/* Column Header */}
                      {viewMode === 'semana' && (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between shrink-0">
                          <div>
                            <p className="text-xs font-bold text-gray-400 uppercase">{date.toLocaleDateString('pt-BR', { weekday: 'short' })}</p>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">{date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}</p>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <span className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-0.5">
                              {items.filter(i => i.isCalculada).length} Atividades feitas
                            </span>
                            <span className="bg-[#5F6C37]/10 text-[#5F6C37] dark:bg-[#5F6C37]/20 dark:text-green-300 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap">
                              {items.filter(i => !i.isCalculada).length}/{kanbanData.maxPerDay} Disp.
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Cards List */}
                      <div className={`flex flex-col gap-3 ${viewMode === 'semana' ? 'flex-1 overflow-y-auto pr-1.5 custom-scrollbar min-h-0 pb-4' : ''}`}>
                        {items.map((item, idx) => {
                          const isExpanded = viewMode === 'dia' || expandedCards.has(item.uc);

                          if (item.isCalculada) {
                            const isReagendado = item.tipoAtividadeFeita === 'reagendado';
                            const isPulado = item.tipoAtividadeFeita === 'pulado';

                            let badgeBg = 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 border-emerald-100/50 dark:border-emerald-900/30';
                            let badgeIcon = <CheckCircle2 size={10} />;
                            let badgeText = 'CONCLUÍDO';
                            let containerClass = 'bg-emerald-50/20 dark:bg-emerald-950/5 border-emerald-200/50 dark:border-emerald-900/30 hover:border-emerald-400/50';
                            let textClass = 'text-emerald-800 dark:text-emerald-300';
                            let badgeMiniBg = 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300';
                            let buttonClass = 'bg-emerald-600 hover:bg-emerald-700';

                            if (isReagendado) {
                              badgeBg = 'bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 border-blue-100/50 dark:border-blue-900/30';
                              badgeIcon = <Calendar size={10} />;
                              badgeText = 'REAGENDADO';
                              containerClass = 'bg-blue-50/20 dark:bg-blue-950/5 border-blue-200/50 dark:border-blue-900/30 hover:border-blue-400/50';
                              textClass = 'text-blue-800 dark:text-blue-300';
                              badgeMiniBg = 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300';
                              buttonClass = 'bg-blue-600 hover:bg-blue-700';
                            } else if (isPulado) {
                              badgeBg = 'bg-orange-50 dark:bg-orange-950 text-orange-600 dark:text-orange-400 border-orange-100/50 dark:border-orange-900/30';
                              badgeIcon = <SkipForward size={10} />;
                              badgeText = 'PULADO';
                              containerClass = 'bg-orange-50/20 dark:bg-orange-950/5 border-orange-200/50 dark:border-orange-900/30 hover:border-orange-400/50';
                              textClass = 'text-orange-800 dark:text-orange-300';
                              badgeMiniBg = 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300';
                              buttonClass = 'bg-orange-600 hover:bg-orange-700';
                            }

                            return (
                              <div
                                key={idx}
                                className={`${containerClass} rounded-xl p-2.5 border flex items-center justify-between gap-2 relative group transition-colors shrink-0`}
                              >
                                <div className="flex flex-col flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                                    <span className={`text-[11px] font-bold ${textClass} tracking-wide flex items-center gap-0.5`}>
                                      <Hash size={10} /> {item.uc}
                                    </span>
                                    <span className={`text-[9px] font-bold ${badgeMiniBg} px-1 rounded`}>
                                      {item.expectedMonth}
                                    </span>
                                    <span className={`text-[9px] font-medium ${badgeBg} px-1 rounded flex items-center gap-0.5 shrink-0`}>
                                      {badgeIcon} {badgeText}
                                    </span>
                                    <ChipStatusRateio status={item.statusRateio} rateio={item.rateioNome} />
                                  </div>
                                  <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 truncate" title={item.nome}>
                                    {item.nome}
                                  </div>
                                </div>
                                <button
                                  onClick={() => {
                                    if (isReagendado) {
                                      setViewingReschedule(item);
                                    } else if (item.fatura?.id) {
                                      onOpenValidation(item.fatura.id, true);
                                    }
                                  }}
                                  className={`px-2 py-1 ${buttonClass} text-white shadow-sm rounded-lg text-[10px] font-bold transition-all flex items-center gap-0.5 shrink-0`}
                                >
                                  Visualizar
                                </button>
                              </div>
                            );
                          }

                          return (
                            <div
                              key={idx}
                              className={`bg-white dark:bg-gray-800 rounded-2xl p-4 border shadow-sm transition-all flex flex-col gap-3 relative overflow-hidden shrink-0
                              ${item.isCalculada ? 'border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/30 dark:bg-emerald-950/10 opacity-90 hover:border-emerald-400' :
                                  item.status === 'critical' ? 'border-red-200 dark:border-red-900/50 hover:border-red-400' :
                                    item.status === 'sem_previsao' ? 'border-amber-200 dark:border-amber-900/50 hover:border-amber-400' :
                                    item.status === 'warning' ? 'border-amber-200 dark:border-amber-900/50 hover:border-amber-400' :
                                      'border-gray-200 dark:border-gray-700 hover:border-[#5F6C37]/30'}`}
                            >
                              {item.isCalculada ? (
                                <div className="absolute top-0 right-0 w-16 h-16 pointer-events-none overflow-hidden">
                                  <div className="absolute top-4 -right-6 w-24 bg-emerald-500 text-white text-[10px] font-bold text-center py-1 rotate-45 shadow-sm z-[1]">
                                    CONCLUÍDO
                                  </div>
                                </div>
                              ) : item.status === 'critical' ? (
                                <div className="absolute top-0 right-0 w-16 h-16 pointer-events-none overflow-hidden">
                                  <div className="absolute top-4 -right-6 w-24 bg-red-500 text-white text-[10px] font-bold text-center py-1 rotate-45 z-[1]">
                                    CRÍTICO
                                  </div>
                                </div>
                              ) : item.status === 'sem_previsao' ? (
                                <div className="absolute top-0 right-0 w-16 h-16 pointer-events-none overflow-hidden">
                                  <div className="absolute top-4 -right-6 w-24 bg-amber-500 text-white text-[8px] font-bold text-center py-1.5 rotate-45 z-[1] leading-none tracking-tight">
                                    SEM HISTÓRICO
                                  </div>
                                </div>
                              ) : null}

                              {(() => {
                                const semana = viewMode === 'semana';
                                const qEntry = item.hasFatura
                                  ? undefined
                                  : queueStatusByUc.get(`${item.outro_numero_uc || item.uc_identificadora}:${item.expectedMonth}`);
                                const roboNaFila = qEntry?.status === 'pendente' || qEntry?.status === 'processando';
                                const motivoRobo = qEntry?.status === 'falhou' ? motivoDoRobo(qEntry.error_details) : null;
                                // Cadastro recusado pela Equatorial: retentar só depois de corrigir.
                                const precisaCorrigirCadastro = motivoRobo === 'dados_incorretos' && !dadosTitularMudaram(qEntry, item.ucData);
                                // Data da busca na mensagem do robô (ex: "buscada em 24/06/2026 às 09:50")
                                const dataBusca = motivoRobo === 'nao_disponivel'
                                  ? String(qEntry?.error_details || '').match(/buscada em (\d{2})\/(\d{2})\/\d{4} às/)
                                  : null;
                                const cpfCnpj = String(item.ucData?.cpf_cnpj_titular || item.cpf_cnpj || '').replace(/\D/g, '');
                                const temDoc = cpfCnpj.length >= 11;
                                const temData = cpfCnpj.length === 11 ? !!item.ucData?.data_nascimento_titular : true;
                                const completo = temDoc && temData;
                                // "Sem Histórico": não há mês previsto pra buscar — o robô baixa a
                                // última fatura disponível só pra semear a previsão de ciclo da UC.
                                const isSemHistorico = item.status === 'sem_previsao';
                                const enviandoRobo = enqueuingForUc === item.uc_identificadora;
                                const enviandoUpload = uploadingForUc === item.uc_identificadora;
                                // Na semana as colunas são estreitas: os botões dividem a largura e o
                                // texto trunca — nada passa da borda do card.
                                const botaoPrincipal = `${semana ? 'flex-1 min-w-0' : 'px-4'} py-1.5 rounded-lg text-[10px] font-bold transition-all flex items-center justify-center gap-1`;
                                const textoBotao = 'truncate min-w-0';
                                const botaoIcone = 'p-1.5 rounded-lg text-gray-400 transition-colors flex items-center gap-1 text-[10px] font-bold uppercase';

                                const acaoPrincipal = item.hasFatura ? (
                                  // Tem fatura: só Calcular. O robô não tem mais o que buscar —
                                  // com fatura ativa no mês a gravação dele é barrada como duplicata.
                                  <button
                                    onClick={() => onOpenValidation(item.fatura.id)}
                                    className={`${botaoPrincipal} text-white shadow-sm ${
                                      item.status === 'critical' ? 'bg-red-500 hover:bg-red-600'
                                      : item.status === 'warning' || item.status === 'sem_previsao' ? 'bg-amber-500 hover:bg-amber-600'
                                      : 'bg-[#5F6C37] hover:bg-[#5F6C37]/90'}`}
                                  >
                                    <span className={textoBotao}>Calcular</span> <ChevronRight size={12} className="shrink-0" />
                                  </button>
                                ) : (
                                  <>
                                    <BotaoHistoricoRobo qEntry={qEntry} />
                                    {roboNaFila ? (
                                      <div title="Aguardando o robô baixar a fatura" className={`${botaoPrincipal} bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400`}>
                                        <Loader2 size={10} className="animate-spin shrink-0" /> <span className={textoBotao}>Robô na fila</span>
                                      </div>
                                    ) : precisaCorrigirCadastro ? (
                                      <button
                                        onClick={() => setCorrigindoTitular({ item, tentativa: qEntry })}
                                        title="A Equatorial recusou o CPF/CNPJ ou a data de nascimento. Corrija o cadastro para liberar uma nova busca."
                                        className={`${botaoPrincipal} bg-orange-500 hover:bg-orange-600 text-white shadow-sm`}
                                      >
                                        <UserCog size={10} className="shrink-0" /> <span className={textoBotao}>Atualizar dados</span>
                                      </button>
                                    ) : motivoRobo ? (
                                      <button
                                        onClick={() => handleRetryEquatorial(qEntry.id, item)}
                                        title={motivoRobo === 'dados_incorretos'
                                          ? 'Cadastro corrigido — clique para o robô buscar com os dados novos'
                                          : `${qEntry.error_details || 'Sem detalhes'} — clique para o robô buscar de novo`}
                                        className={`${botaoPrincipal} border border-gray-200 text-gray-600 hover:border-green-600 hover:text-green-700 dark:border-gray-600 dark:text-gray-300 dark:hover:text-green-400`}
                                      >
                                        <RotateCw size={10} className="shrink-0" /> <span className={textoBotao}>Retentar</span>
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => completo && handleEnfileirarEquatorial(item, isSemHistorico ? 'ultima_disponivel' : 'mes_especifico')}
                                        disabled={!completo || enviandoRobo}
                                        title={
                                          !temDoc ? 'Falta CPF/CNPJ na UC' :
                                          !temData ? 'Falta data de nascimento na UC' :
                                          isSemHistorico ? 'Busca a fatura mais recente disponível para prever o ciclo desta UC' :
                                          'Adicionar como prioridade na fila do robô'
                                        }
                                        className={`${botaoPrincipal} shadow-sm ${completo ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed dark:bg-gray-700 dark:border-gray-600'}`}
                                      >
                                        {enviandoRobo
                                          ? <Loader2 size={10} className="animate-spin shrink-0" />
                                          : completo ? <Bot size={10} className="shrink-0" /> : <AlertTriangle size={10} className="shrink-0" />}
                                        <span className={textoBotao}>{enviandoRobo ? 'Enviando...' : isSemHistorico ? 'Última fatura' : 'Equatorial'}</span>
                                      </button>
                                    )}
                                    {uploadSuccessUc === item.uc_identificadora ? (
                                      <div className={`${botaoPrincipal} bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400`}>
                                        <CheckCircle2 size={10} className="shrink-0" /> <span className={textoBotao}>Na fila</span>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => handleUploadClick(item.uc_identificadora)}
                                        disabled={enviandoUpload}
                                        className={`${botaoPrincipal} bg-blue-600 text-white hover:bg-blue-700 shadow-sm disabled:opacity-50`}
                                      >
                                        {enviandoUpload ? <Loader2 size={10} className="animate-spin shrink-0" /> : <Upload size={10} className="shrink-0" />}
                                        <span className={textoBotao}>{enviandoUpload ? 'Enviando...' : 'Upload'}</span>
                                      </button>
                                    )}
                                  </>
                                );

                                // Mesmos ícones da lista; no Dia, com espaço, o rótulo aparece ao lado.
                                const acoesSecundarias = (
                                  <div className="flex items-center gap-0.5">
                                    <button
                                      onClick={() => { setRescheduleItem(item); setNewScheduleDate(dateStr); }}
                                      title="Reagendar"
                                      aria-label="Reagendar"
                                      className={`${botaoIcone} hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20`}
                                    >
                                      <Calendar size={14} />{!semana && 'Reagendar'}
                                    </button>
                                    {canTransferItem(item) && (
                                      <button
                                        onClick={() => { setTransferItem(item); setTransferTargetId(''); }}
                                        title="Transferir responsável"
                                        aria-label="Transferir responsável"
                                        className={`${botaoIcone} hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20`}
                                      >
                                        <ArrowRightLeft size={14} />{!semana && 'Transferir'}
                                      </button>
                                    )}
                                    <button
                                      onClick={() => setSkippingItem(item)}
                                      title="Não precisa calcular"
                                      aria-label="Não precisa calcular"
                                      className={`${botaoIcone} hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20`}
                                    >
                                      <SkipForward size={14} />{!semana && 'Não precisa calcular'}
                                    </button>
                                  </div>
                                );

                                return (
                                  <div className="flex flex-col gap-2.5">
                                    {/* Identificação — o pr-12 deixa livre o canto da faixa CRÍTICO/SEM HISTÓRICO */}
                                    <div className="flex flex-col gap-1 pr-12 min-w-0">
                                      <div className="flex flex-wrap items-center gap-1.5">
                                        <Hash size={13} className="text-[#5F6C37] shrink-0" />
                                        <span className="text-xs font-bold text-[#5F6C37] tracking-wider">UC {item.uc}</span>
                                        {item.outro_numero_uc && (
                                          <span className="text-[10px] font-bold text-slate-500 bg-slate-100 dark:bg-slate-700 dark:text-slate-300 px-1.5 rounded tracking-wider">
                                            {item.outro_numero_uc}
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate" title={item.nome}>{item.nome || '—'}</p>
                                    </div>

                                    {/* Contexto: referência, rateio com o status da UC nele, responsável e robô */}
                                    <div className="flex flex-col gap-1.5">
                                      <div className="flex flex-wrap items-center gap-1.5">
                                        <span className="px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 text-[10px] font-bold uppercase whitespace-nowrap">
                                          Ref {item.expectedMonth}
                                        </span>
                                        <span className="flex items-center gap-1 text-[10px] font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                          <Calendar size={11} className="text-gray-400 shrink-0" /> Rateio {item.rateioNome || '—'}
                                        </span>
                                        <ChipStatusRateio status={item.statusRateio} rateio={item.rateioNome} />
                                        {item.atrasadaEquatorial ? (
                                          <ChipFaturaAtrasada previsao={item.previsaoFatura} />
                                        ) : item.isPredictive && item.previsaoFatura?.perfil === 'impressa_na_hora' ? (
                                          <ChipImpressaNaHora />
                                        ) : null}
                                        {item.prazoCurto && !item.atrasadaEquatorial ? (
                                          <ChipPrazoCurto chegada={item.previsaoFatura?.chegada} limite={chaveData(item.criticoJanelaIso)} />
                                        ) : null}
                                      </div>
                                      {(item.responsavel_nome || motivoRobo) && (
                                        <div className="flex flex-wrap items-center gap-1.5">
                                          {item.responsavel_nome && (
                                            <span className="flex items-center gap-1 min-w-0 max-w-full text-[10px] font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                                              <User size={10} className="shrink-0" /> <span className="truncate min-w-0">{item.responsavel_nome}</span>
                                            </span>
                                          )}
                                          {motivoRobo && (
                                            <ChipMotivoRobo
                                              motivo={motivoRobo}
                                              detalhe={qEntry?.error_details}
                                              texto={dataBusca ? `Não disponível · buscada ${dataBusca[1]}/${dataBusca[2]}` : undefined}
                                            />
                                          )}
                                        </div>
                                      )}
                                    </div>

                                    {/* Ações: na semana, principal em cima e secundárias embaixo; no dia, lado a lado */}
                                    {semana ? (
                                      <div className="flex flex-col gap-2 pt-2.5 border-t border-gray-100 dark:border-gray-700">
                                        <div className="flex items-stretch gap-1.5">{acaoPrincipal}</div>
                                        <div className="flex items-center justify-between">
                                          <button
                                            onClick={() => toggleExpand(item.uc)}
                                            className="text-gray-400 hover:text-[#5F6C37] text-[10px] font-bold uppercase flex items-center gap-0.5 transition-colors"
                                          >
                                            {isExpanded ? 'Recolher' : 'Detalhes'}
                                            <ChevronDown size={12} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                          </button>
                                          {acoesSecundarias}
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex flex-wrap items-center justify-between gap-2 pt-2.5 border-t border-gray-100 dark:border-gray-700">
                                        {acoesSecundarias}
                                        <div className="flex items-stretch gap-1.5">{acaoPrincipal}</div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}

                              {/* FULL CARD CONTENTS (Only when expanded) */}
                              {isExpanded && (
                                <div className="mt-2 flex flex-col gap-3 animate-in fade-in slide-in-from-top-2">
                                  {/* Info Panel: Prediction or Real */}
                                  {item.isIncompleto && !item.isPredictive ? (
                                    <div className="flex items-center gap-3 p-3 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 rounded-xl border border-red-100 dark:border-red-900/30">
                                      <AlertTriangle size={18} className="shrink-0" />
                                      <p className="text-[10px] font-medium leading-tight">Dados incompletos na fatura enviada.</p>
                                    </div>
                                  ) : (
                                    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
                                      <h4 className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                        {item.isPredictive ? <><Calendar size={10} className="text-blue-500" /> Janela Prevista</> : <><Calendar size={10} className="text-[#5F6C37]" /> Janela de Faturamento</>}
                                      </h4>
                                      {(!item.inicioJanelaIso && !item.fimJanelaIso) ? (
                                        <div className="text-center py-1 text-gray-400 text-[10px] italic">
                                          Sem histórico.
                                        </div>
                                      ) : (
                                        <div className="grid grid-cols-3 gap-2 text-center text-[10px]">
                                          <div title={!item.isPredictive || !item.previsaoFatura ? undefined
                                            : item.previsaoFatura.perfil === 'impressa_na_hora' ? 'Fatura impressa na hora: costuma existir no dia da leitura; entra na janela no dia útil seguinte'
                                              : 'Quando a fatura costuma chegar: 4º dia útil depois da leitura prevista'}>
                                            <p className="text-gray-500 dark:text-gray-400 mb-0.5">Início</p>
                                            <p className="font-semibold text-gray-900 dark:text-white">{formatDateString(item.inicioJanelaIso)}</p>
                                          </div>
                                          <div title={!item.isPredictive || !item.previsaoFatura ? undefined
                                            : item.previsaoFatura.perfil === 'impressa_na_hora' ? 'Apresentação prevista: no próprio dia da leitura'
                                              : 'Apresentação prevista: 7º dia útil depois da leitura prevista'}>
                                            <p className="text-gray-500 dark:text-gray-400 mb-0.5">Fim</p>
                                            <p className="font-semibold text-gray-900 dark:text-white">{formatDateString(item.fimJanelaIso)}</p>
                                          </div>
                                          <div>
                                            <p className="text-gray-500 dark:text-gray-400 mb-0.5">Crítico</p>
                                            <p className={`font-bold ${item.status === 'critical' ? 'text-red-500' : item.status === 'warning' ? 'text-amber-500' : 'text-gray-900 dark:text-white'}`}>
                                              {formatDateString(item.criticoJanelaIso)}
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Extended UC and History Info */}
                                  {(() => {
                                    const itemUcClean1 = cleanUC(item.uc);
                                    const itemUcClean2 = cleanUC(item.outro_numero_uc);
                                    const ucDb = unidadesConsumidoras.find(u => {
                                      const dbUc1 = cleanUC(u.numero_uc);
                                      const dbUc2 = cleanUC(u.outro_numero_uc);
                                      return (dbUc1 && itemUcClean1 && dbUc1 === itemUcClean1) ||
                                        (dbUc2 && itemUcClean1 && dbUc2 === itemUcClean1) ||
                                        (dbUc1 && itemUcClean2 && dbUc1 === itemUcClean2) ||
                                        (dbUc2 && itemUcClean2 && dbUc2 === itemUcClean2);
                                    });
                                    const bf = item.baseFatura;
                                    const isEditing = ucDb && editingUcId === ucDb.id;

                                    const safeDateStr = (val: any) => {
                                      if (!val) return '—';
                                      // Trata strings no formato puro YYYY-MM-DD sem converter timezone
                                      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
                                        const [y, m, d] = val.split('-');
                                        return `${d}/${m}/${y}`;
                                      }
                                      const d = new Date(val);
                                      return isNaN(d.getTime()) ? '—' : formatDateString(d);
                                    };

                                    return (
                                      <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-3 border border-gray-100 dark:border-gray-700 flex flex-col gap-3 text-[10px]">

                                        {(() => {
                                          const mesOptions = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
                                          const isEditingHist = ucDb && editingHistUcId === ucDb.id;

                                          const startEditHist = () => {
                                            if (!ucDb) return;
                                            const [mesAtual, anoAtual] = (ucDb.hist_manual_mes_referencia || '').split('/');
                                            setEditingHistUcId(ucDb.id);
                                            setHistFormData({
                                              hist_manual_mes: mesAtual || '',
                                              hist_manual_ano: anoAtual || String(new Date().getFullYear()),
                                              hist_manual_leitura_anterior: ucDb.hist_manual_leitura_anterior || '',
                                              hist_manual_leitura_atual: ucDb.hist_manual_leitura_atual || '',
                                              hist_manual_proxima_leitura: ucDb.hist_manual_proxima_leitura || '',
                                              hist_manual_data_apresentacao: ucDb.hist_manual_data_apresentacao || '',
                                              hist_manual_data_vencimento: ucDb.hist_manual_data_vencimento || '',
                                            });
                                          };

                                          if (isEditingHist) {
                                            return (
                                              <div>
                                                <h4 className="font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                                                  <FileText size={10} /> Dados da Última Fatura (manual)
                                                </h4>
                                                <div className="bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-600 flex flex-col gap-2">
                                                  <div>
                                                    <label className="block text-[9px] font-semibold text-gray-400 uppercase">Mês de Referência</label>
                                                    <div className="grid grid-cols-2 gap-1.5 mt-0.5">
                                                      <select value={histFormData.hist_manual_mes || ''} onChange={(e) => setHistFormData({ ...histFormData, hist_manual_mes: e.target.value })} className="w-full bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">
                                                        <option value="">Mês</option>
                                                        {mesOptions.map(m => <option key={m} value={m}>{m}</option>)}
                                                      </select>
                                                      <input type="number" value={histFormData.hist_manual_ano || ''} onChange={(e) => setHistFormData({ ...histFormData, hist_manual_ano: e.target.value })} className="w-full bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5" placeholder="Ano" />
                                                    </div>
                                                  </div>
                                                  <div className="grid grid-cols-2 gap-1.5">
                                                    <div>
                                                      <label className="block text-[9px] font-semibold text-gray-400 uppercase">Leitura Anterior</label>
                                                      <input type="date" value={histFormData.hist_manual_leitura_anterior || ''} onChange={(e) => setHistFormData({ ...histFormData, hist_manual_leitura_anterior: e.target.value })} className="w-full bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 mt-0.5" />
                                                    </div>
                                                    <div>
                                                      <label className="block text-[9px] font-semibold text-gray-400 uppercase">Leitura Atual</label>
                                                      <input type="date" value={histFormData.hist_manual_leitura_atual || ''} onChange={(e) => setHistFormData({ ...histFormData, hist_manual_leitura_atual: e.target.value })} className="w-full bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 mt-0.5" />
                                                    </div>
                                                  </div>
                                                  <div className="grid grid-cols-2 gap-1.5">
                                                    <div>
                                                      <label className="block text-[9px] font-semibold text-gray-400 uppercase">Data Apresentação</label>
                                                      <input type="date" value={histFormData.hist_manual_data_apresentacao || ''} onChange={(e) => setHistFormData({ ...histFormData, hist_manual_data_apresentacao: e.target.value })} className="w-full bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 mt-0.5" />
                                                    </div>
                                                    <div>
                                                      <label className="block text-[9px] font-semibold text-gray-400 uppercase">Data Vencimento</label>
                                                      <input type="date" value={histFormData.hist_manual_data_vencimento || ''} onChange={(e) => setHistFormData({ ...histFormData, hist_manual_data_vencimento: e.target.value })} className="w-full bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 mt-0.5" />
                                                    </div>
                                                  </div>
                                                  <div>
                                                    <label className="block text-[9px] font-semibold text-gray-400 uppercase">Próxima Leitura (prevista)</label>
                                                    <input type="date" value={histFormData.hist_manual_proxima_leitura || ''} onChange={(e) => setHistFormData({ ...histFormData, hist_manual_proxima_leitura: e.target.value })} className="w-full bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 mt-0.5" />
                                                  </div>
                                                  <div className="flex gap-3 justify-end pt-1 border-t border-gray-100 dark:border-gray-700">
                                                    <button onClick={() => { setEditingHistUcId(null); setHistFormData({}); }} className="text-gray-400 hover:text-gray-600 font-semibold" disabled={savingHist}>Cancelar</button>
                                                    <button onClick={() => handleSaveHistorico(ucDb.id)} className="text-[#5F6C37] font-bold flex items-center gap-1" disabled={savingHist}>
                                                      {savingHist ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />} Salvar
                                                    </button>
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          }

                                          if (bf) {
                                            return (
                                              <div>
                                                <h4 className="font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5 flex items-center justify-between gap-1">
                                                  <span className="flex items-center gap-1"><FileText size={10} /> Última Fatura ({bf.mes_referencia})</span>
                                                  {item.isHistoricoManual && ucDb && (
                                                    <span className="flex items-center gap-1.5 normal-case font-medium">
                                                      <span className="text-[8px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-1 py-0.5 rounded">Informado manualmente</span>
                                                      <button onClick={startEditHist} className="text-[#5F6C37] hover:underline flex items-center gap-0.5 font-semibold"><Edit2 size={9} /> Editar</button>
                                                    </span>
                                                  )}
                                                </h4>
                                                <div className={`grid gap-2 ${viewMode === 'semana' ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-5'}`}>
                                                  <div><span className="text-gray-400 block">Leit. Ant</span><span className="font-semibold">{safeDateStr(bf.leitura_anterior)}</span></div>
                                                  <div><span className="text-gray-400 block">Leit. Atual</span><span className="font-semibold">{safeDateStr(bf.leitura_atual)}</span></div>
                                                  <div><span className="text-gray-400 block">Apresentação</span><span className="font-semibold">{safeDateStr(bf.data_apresentacao)}</span></div>
                                                  <div><span className="text-gray-400 block">Vencimento</span><span className="font-semibold">{safeDateStr(bf.data_vencimento)}</span></div>
                                                  <div><span className="text-gray-400 block">Dias de Consumo</span><span className="font-semibold">{item.diasEntreLeituras !== null ? item.diasEntreLeituras : '—'}</span></div>
                                                </div>
                                              </div>
                                            );
                                          }

                                          return (
                                            <div className="flex items-center justify-between gap-2 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 rounded-lg p-2">
                                              <p className="text-amber-700 dark:text-amber-400 font-medium leading-tight">
                                                Sem histórico de faturas para esta UC.
                                                {!ucDb && ' Cadastre a UC abaixo antes de informar os dados.'}
                                              </p>
                                              {ucDb && (
                                                <button onClick={startEditHist} className="shrink-0 px-2 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-[9px] font-bold flex items-center gap-1">
                                                  <FileText size={10} /> Adicionar última fatura
                                                </button>
                                              )}
                                            </div>
                                          );
                                        })()}

                                        {(() => {
                                          const displayUc = ucDb || {
                                            nome_cliente: item.nome || bf?.cliente_nome || '',
                                            cpf_cnpj_cliente: item.cpf_cnpj || bf?.cliente_cpf_cnpj || '',
                                            titular_fatura: bf?.cliente_nome || '',
                                            cpf_cnpj_titular: bf?.cliente_cpf_cnpj || '',
                                            data_nascimento_titular: null,
                                            num_medidor: bf?.numero_medidor || '',
                                            unidade_geradora: '',
                                            percent_rateio: null
                                          };
                                          const isNewUc = !ucDb;
                                          const editId = isNewUc ? item.uc : ucDb.id;
                                          const isCurrentlyEditing = editingUcId === editId;
                                          const cadastroTravadoUc = !isNewUc && !podeEditarCadastroDaUc(profile, ucDb);

                                          return (
                                            <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
                                              <div className="flex items-center justify-between mb-2">
                                                <h4 className="font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                                  <User size={10} /> Dados da UC
                                                  {isNewUc && <span className="text-[8px] bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded ml-1">NÃO CADASTRADA</span>}
                                                </h4>
                                                {!isCurrentlyEditing ? (
                                                  <button onClick={() => {
                                                    setEditingUcId(editId);
                                                    setEditFormData(isNewUc ? {
                                                      numero_uc: item.uc,
                                                      outro_numero_uc: item.outro_numero_uc || '',
                                                      nome_cliente: item.nome || bf?.cliente_nome || '',
                                                      cpf_cnpj_cliente: item.cpf_cnpj || bf?.cliente_cpf_cnpj || '',
                                                      titular_fatura: bf?.cliente_nome || '',
                                                      cpf_cnpj_titular: bf?.cliente_cpf_cnpj || '',
                                                    } : ucDb);
                                                  }} className="text-[#5F6C37] hover:underline flex items-center gap-1 font-semibold">
                                                    <Edit2 size={10} /> {isNewUc ? 'Cadastrar' : 'Editar'}
                                                  </button>
                                                ) : (
                                                  <div className="flex gap-2">
                                                    <button onClick={() => setEditingUcId(null)} className="text-gray-400 hover:text-gray-600 font-semibold" disabled={savingUc}>Cancelar</button>
                                                    <button onClick={() => handleSaveUc(editId, isNewUc)} className="text-[#5F6C37] font-bold flex items-center gap-1" disabled={savingUc}>
                                                      {savingUc ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />} Salvar
                                                    </button>
                                                  </div>
                                                )}
                                              </div>

                                              {!isCurrentlyEditing ? (
                                                <div className="grid grid-cols-1 gap-2">
                                                  <div className={`bg-white dark:bg-gray-800 p-1.5 rounded border border-gray-100 dark:border-gray-700 ${isNewUc ? 'opacity-60' : ''}`}>
                                                    <p className="font-semibold text-[9px] text-gray-400 uppercase">Associado</p>
                                                    <p className="truncate font-medium">{displayUc.nome_cliente || '—'}</p>
                                                    <p className="text-gray-500 text-[9px]">{normalizeCpfCnpj(displayUc.cpf_cnpj_cliente)}</p>
                                                  </div>
                                                  <div className={`bg-white dark:bg-gray-800 p-1.5 rounded border border-gray-100 dark:border-gray-700 ${isNewUc ? 'opacity-60' : ''}`}>
                                                    <p className="font-semibold text-[9px] text-gray-400 uppercase">Titular / Medidor</p>
                                                    <p className="truncate font-medium">{displayUc.titular_fatura || '—'}</p>
                                                    <p className="text-gray-500 text-[9px]">{normalizeCpfCnpj(displayUc.cpf_cnpj_titular)} {displayUc.data_nascimento_titular ? `• Nasc: ${safeDateStr(displayUc.data_nascimento_titular)}` : ''}</p>
                                                    <p className="text-gray-500 text-[9px]">Medidor: <span className="font-medium text-gray-700 dark:text-gray-300">{displayUc.num_medidor || '—'}</span></p>
                                                  </div>
                                                  <div className={`bg-white dark:bg-gray-800 p-1.5 rounded border border-gray-100 dark:border-gray-700 ${isNewUc ? 'opacity-60' : ''}`}>
                                                    <p className="font-semibold text-[9px] text-gray-400 uppercase">Rateio UG</p>
                                                    <p>UG: <span className="font-medium">{displayUc.unidade_geradora || '—'}</span> | % Rateio: <span className="font-medium">{displayUc.percent_rateio ? `${displayUc.percent_rateio}%` : '—'}</span></p>
                                                  </div>
                                                </div>
                                              ) : (
                                                <div className="bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-600 flex flex-col gap-2">
                                                  {cadastroTravadoUc ? (
                                                    <p className="text-[9px] text-amber-700 dark:text-amber-400">Nome, CPF/CNPJ e titular: só o Financeiro altera. Medidor e rateio continuam liberados.</p>
                                                  ) : null}
                                                  <fieldset disabled={cadastroTravadoUc} className="min-w-0 disabled:opacity-60">
                                                    <label className="block text-[9px] font-semibold text-gray-400 uppercase">Associado</label>
                                                    <input type="text" value={editFormData.nome_cliente || ''} onChange={(e) => setEditFormData({ ...editFormData, nome_cliente: e.target.value })} className="w-full bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 mt-0.5" placeholder="Nome" />
                                                    <input type="text" value={editFormData.cpf_cnpj_cliente || ''} onChange={(e) => setEditFormData({ ...editFormData, cpf_cnpj_cliente: e.target.value })} className="w-full bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 mt-1" placeholder="CPF/CNPJ" />
                                                  </fieldset>
                                                  <div>
                                                    <label className="block text-[9px] font-semibold text-gray-400 uppercase">Titular</label>
                                                    <input type="text" disabled={cadastroTravadoUc} value={editFormData.titular_fatura || ''} onChange={(e) => setEditFormData({ ...editFormData, titular_fatura: e.target.value })} className="w-full bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 mt-0.5 disabled:opacity-60" placeholder="Nome do Titular" />
                                                    <input type="text" disabled={cadastroTravadoUc} value={editFormData.cpf_cnpj_titular || ''} onChange={(e) => setEditFormData({ ...editFormData, cpf_cnpj_titular: e.target.value })} className="w-full bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 mt-1 disabled:opacity-60" placeholder="CPF/CNPJ" />
                                                    <div className="grid grid-cols-2 gap-1.5 mt-1">
                                                      <input type="date" disabled={cadastroTravadoUc} value={editFormData.data_nascimento_titular || ''} onChange={(e) => setEditFormData({ ...editFormData, data_nascimento_titular: e.target.value || null })} className="w-full bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 text-gray-600" title="Data de Nascimento" />
                                                      <input type="text" value={editFormData.num_medidor || ''} onChange={(e) => setEditFormData({ ...editFormData, num_medidor: e.target.value })} className="w-full bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5" placeholder="Medidor" />
                                                    </div>
                                                  </div>
                                                  <div>
                                                    <label className="block text-[9px] font-semibold text-gray-400 uppercase">UG e Rateio</label>
                                                    <input type="text" value={editFormData.unidade_geradora || ''} onChange={(e) => setEditFormData({ ...editFormData, unidade_geradora: e.target.value })} className="w-full bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 mt-0.5" placeholder="Unidade Geradora" />
                                                    <input type="number" step="0.01" value={editFormData.percent_rateio || ''} onChange={(e) => setEditFormData({ ...editFormData, percent_rateio: e.target.value ? parseFloat(e.target.value) : null })} className="w-full bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 mt-1" placeholder="% de Rateio" />
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        </>)}

        {/* Sub-modal: Motivo Skip */}
        {skippingItem && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in shadow-2xl" style={{ zIndex: 8100 }} {...propsFundoModal(() => { setSkippingItem(null); limparMotivoSkip(); })}>
            {/* Altura presa à tela e só o meio rola, para os botões não saírem da tela. */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border dark:border-gray-700 overflow-hidden animate-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
              <div className="flex-1 min-h-0 overflow-y-auto p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 shrink-0 bg-amber-100 dark:bg-amber-900/30 text-amber-600 rounded-full flex items-center justify-center">
                    <AlertCircle size={20} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Pular Cálculo e Enviar Cobrança</h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Você está marcando a UC <span className="font-bold text-[#5F6C37]">{skippingItem.outro_numero_uc || skippingItem.uc}</span> como não necessitando de cálculo para <span className="font-bold">{skippingItem.expectedMonth}</span>.
                </p>

                {/* Sem fatura ainda (a Janela vai criá-la já pulada), não há injeção a conferir.
                    Se uma fatura real chegou nesse meio-tempo, o banco confere a injeção dela. */}
                <PularCalculoCampo faturaId={skippingItem.fatura?.id ?? null} onChange={setEstadoPulo} />
              </div>

              <div className="shrink-0 p-6 bg-gray-50 dark:bg-gray-800/50 flex gap-3 border-t dark:border-gray-700">
                <button
                  onClick={() => { setSkippingItem(null); limparMotivoSkip(); }}
                  className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-white rounded-xl font-bold hover:bg-gray-300 transition-all text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSkipCalculation}
                  disabled={loading || !estadoPulo.campos}
                  className="flex-1 py-3 bg-[#5F6C37] text-white rounded-xl font-bold hover:bg-[#5F6C37]/90 transition-all disabled:opacity-50 disabled:grayscale flex items-center justify-center gap-2 text-sm shadow-lg shadow-[#5F6C37]/20"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : 'Confirmar Ação'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sub-modal: Reagendamento */}
        {rescheduleItem && (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in shadow-2xl"
            style={{ zIndex: 8100 }}
            onMouseDown={e => { rescheduleBackdropMouseDownRef.current = e.target === e.currentTarget; }}
            onClick={() => {
              if (rescheduleBackdropMouseDownRef.current) { setRescheduleItem(null); setNewScheduleDate(''); setMotivoReagendamento(''); setComprovanteFile(null); }
              rescheduleBackdropMouseDownRef.current = false;
            }}
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md border dark:border-gray-700 overflow-hidden animate-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
              <div className="p-6">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-full flex items-center justify-center mb-4">
                  <Calendar size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Reagendar Cobrança</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  Você está reagendando a UC <span className="font-bold text-[#5F6C37]">{rescheduleItem.outro_numero_uc || rescheduleItem.uc}</span> para uma nova data de cálculo.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">
                      Nova Data Alvo
                    </label>
                    <input
                      type="date"
                      value={newScheduleDate}
                      onChange={(e) => setNewScheduleDate(e.target.value)}
                      className="w-full bg-gray-100 dark:bg-gray-700 border-none rounded-xl p-3 text-sm focus:ring-2 focus:ring-[#5F6C37] outline-none text-gray-900 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">
                      Motivo do Reagendamento
                    </label>
                    <textarea
                      value={motivoReagendamento}
                      onChange={(e) => setMotivoReagendamento(e.target.value)}
                      placeholder="Ex: Fatura não disponível no site da distribuidora..."
                      className="w-full bg-gray-100 dark:bg-gray-700 border-none rounded-xl p-3 text-sm focus:ring-2 focus:ring-[#5F6C37] min-h-[80px] outline-none text-gray-900 dark:text-white resize-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">
                      Print da Prova (Comprovante)
                    </label>
                    <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl cursor-pointer hover:border-[#5F6C37] hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-all group">
                      <div className="flex flex-col items-center justify-center pt-3 pb-3 text-center px-2">
                        {comprovanteFile ? (
                          <>
                            <Image size={22} className="text-emerald-500 mb-1 animate-pulse" />
                            <p className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 truncate max-w-[220px]">
                              {comprovanteFile.name}
                            </p>
                            <p className="text-[10px] text-gray-400">
                              {(comprovanteFile.size / 1024).toFixed(0)} KB - Clique para trocar
                            </p>
                          </>
                        ) : (
                          <>
                            <Upload size={22} className="text-gray-400 group-hover:text-[#5F6C37] mb-1" />
                            <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400">
                              Clique para anexar o print
                            </p>
                            <p className="text-[10px] text-gray-400">
                              Formatos de imagem (PNG, JPG)
                            </p>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setComprovanteFile(e.target.files[0]);
                          }
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-gray-50 dark:bg-gray-800/50 flex gap-3 border-t dark:border-gray-700">
                <button
                  onClick={() => {
                    setRescheduleItem(null);
                    setNewScheduleDate('');
                    setMotivoReagendamento('');
                    setComprovanteFile(null);
                  }}
                  className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-white rounded-xl font-bold hover:bg-gray-300 transition-all text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleReschedule}
                  disabled={loading || !newScheduleDate || !motivoReagendamento.trim() || !comprovanteFile}
                  className="flex-1 py-3 bg-[#5F6C37] text-white rounded-xl font-bold hover:bg-[#5F6C37]/90 transition-all disabled:opacity-50 disabled:grayscale flex items-center justify-center gap-2 text-sm shadow-lg shadow-[#5F6C37]/20"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sub-modal: Transferir Cobrança */}
        {transferItem && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" style={{ zIndex: 8100 }} {...propsFundoModal(() => { setTransferItem(null); setTransferTargetId(''); })}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md border dark:border-gray-700 overflow-hidden animate-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex justify-between items-start mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 text-purple-600 rounded-full flex items-center justify-center">
                      <ArrowRightLeft size={20} />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900 dark:text-white">Transferir Cobrança</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        UC: <span className="font-semibold text-gray-700 dark:text-gray-300">{transferItem.outro_numero_uc || transferItem.uc}</span>
                        {' · '}
                        <span className="font-semibold text-gray-700 dark:text-gray-300">{transferItem.expectedMonth}</span>
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setTransferItem(null); setTransferTargetId(''); }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-3 border dark:border-gray-700 text-xs">
                    <p className="text-gray-400 font-bold uppercase text-[9px] mb-0.5">Cliente</p>
                    <p className="font-semibold text-gray-800 dark:text-gray-200 truncate">{transferItem.nome}</p>
                  </div>

                  <div className="bg-purple-50 dark:bg-purple-900/10 rounded-xl p-3 border border-purple-100 dark:border-purple-900/30 flex items-center gap-2 text-xs">
                    <User size={14} className="text-purple-500 shrink-0" />
                    <div>
                      <p className="text-gray-400 font-bold uppercase text-[9px] mb-0.5">Responsável Atual</p>
                      <p className="font-semibold text-purple-700 dark:text-purple-300">{transferItem.responsavel_nome || 'Não atribuído'}</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">
                      Transferir Para
                    </label>
                    <select
                      value={transferTargetId}
                      onChange={(e) => setTransferTargetId(e.target.value)}
                      className="w-full bg-gray-100 dark:bg-gray-700 border-none rounded-xl px-4 py-2.5 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Selecione um analista...</option>
                      {analysts
                        .filter(a => a.id !== transferItem.responsavel_id)
                        .map(analyst => (
                          <option key={analyst.id} value={analyst.id}>
                            {analyst.full_name || analyst.email}
                          </option>
                        ))
                      }
                    </select>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-gray-50 dark:bg-gray-800/50 flex gap-3 border-t dark:border-gray-700">
                <button
                  onClick={() => { setTransferItem(null); setTransferTargetId(''); }}
                  className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-white rounded-xl font-bold hover:bg-gray-300 transition-all text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleTransfer}
                  disabled={transferring || !transferTargetId}
                  className="flex-1 py-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 transition-all disabled:opacity-50 disabled:grayscale flex items-center justify-center gap-2 text-sm shadow-lg shadow-purple-600/20"
                >
                  {transferring ? <Loader2 size={18} className="animate-spin" /> : <><ArrowRightLeft size={16} /> Confirmar</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sub-modal: Corrigir cadastro recusado pela Equatorial */}
        {corrigindoTitular && (
          <AtualizarDadosTitularModal
            item={corrigindoTitular.item}
            tentativa={corrigindoTitular.tentativa}
            onClose={() => setCorrigindoTitular(null)}
            onSalvo={() => {
              const ucId = corrigindoTitular.item.ucData?.id;
              setCorrigindoTitular(null);
              onActionComplete(ucId ? { ucIds: [Number(ucId)] } : undefined);
              refreshQueueRef.current();
            }}
          />
        )}

        {/* Sub-modal: Relatório de tentativas do robô Equatorial */}
        {historicoRoboItem && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" style={{ zIndex: 8100 }} {...propsFundoModal(() => setHistoricoRoboItem(null))}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg border dark:border-gray-700 overflow-hidden animate-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 rounded-full flex items-center justify-center">
                      <History size={20} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">Tentativas do Robô</h3>
                      <p className="text-xs text-gray-400">
                        UC {historicoRoboItem.uc_codigo} · {historicoRoboItem.mes_referencia}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setHistoricoRoboItem(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                  {[...(historicoRoboItem.historico_execucoes || [])].reverse().map((entrada: any, idx: number) => {
                    const dataFmt = entrada.timestamp
                      ? new Date(entrada.timestamp).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
                      : '—';
                    const acaoManual = entrada.status === 'reiniciado' || entrada.status === 'dados_atualizados';
                    return (
                      <div key={idx} className="rounded-xl p-3 border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30 text-xs">
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <div className="flex items-center gap-1.5 min-w-0">
                            {!acaoManual && (
                              <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 shrink-0">
                                Tentativa {entrada.tentativa ?? '?'}
                              </span>
                            )}
                            <ChipEntradaRobo status={entrada.status} mensagem={entrada.mensagem} />
                            {entrada.status === 'pendente' && (
                              <span className="text-[10px] text-gray-400 shrink-0">o robô tenta de novo</span>
                            )}
                          </div>
                          <span className="text-gray-400 text-[10px] shrink-0">{dataFmt}</span>
                        </div>
                        <p className="text-gray-600 dark:text-gray-300">{entrada.mensagem || 'Sem detalhes.'}</p>
                      </div>
                    );
                  })}
                  {(!historicoRoboItem.historico_execucoes || historicoRoboItem.historico_execucoes.length === 0) && (
                    <p className="text-sm text-gray-400 text-center py-4">Nenhuma tentativa registrada ainda.</p>
                  )}
                </div>
              </div>

              <div className="p-6 bg-gray-50 dark:bg-gray-800/50 border-t dark:border-gray-700">
                <button
                  onClick={() => setHistoricoRoboItem(null)}
                  className="w-full py-3 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-white rounded-xl font-bold hover:bg-gray-300 transition-all text-sm"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sub-modal: Detalhes do Reagendamento */}
        {viewingReschedule && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in shadow-2xl" style={{ zIndex: 8100 }} {...propsFundoModal(() => setViewingReschedule(null))}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg border dark:border-gray-700 overflow-hidden animate-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-full flex items-center justify-center">
                      <Calendar size={20} />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900 dark:text-white">Detalhes do Reagendamento</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        UC: <span className="font-semibold text-gray-700 dark:text-gray-300">{viewingReschedule.outro_numero_uc || viewingReschedule.uc}</span>
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setViewingReschedule(null)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-4 my-2">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="bg-gray-50 dark:bg-gray-700/30 p-2.5 rounded-xl border dark:border-gray-700">
                      <p className="text-gray-400 font-bold uppercase text-[9px] mb-0.5">Cliente</p>
                      <p className="font-semibold text-gray-800 dark:text-gray-200 truncate">{viewingReschedule.nome}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/30 p-2.5 rounded-xl border dark:border-gray-700">
                      <p className="text-gray-400 font-bold uppercase text-[9px] mb-0.5">Mês de Referência</p>
                      <p className="font-semibold text-gray-800 dark:text-gray-200">{viewingReschedule.expectedMonth}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/30 p-2.5 rounded-xl border dark:border-gray-700">
                      <p className="text-gray-400 font-bold uppercase text-[9px] mb-0.5">Agendado Para</p>
                      <p className="font-semibold text-blue-600 dark:text-blue-400">
                        {viewingReschedule.fatura?.data_cobranca_agendada
                          ? new Date(viewingReschedule.fatura.data_cobranca_agendada + 'T12:00:00').toLocaleDateString('pt-BR')
                          : 'Não definida'}
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/30 p-2.5 rounded-xl border dark:border-gray-700">
                      <p className="text-gray-400 font-bold uppercase text-[9px] mb-0.5">Reagendado Por</p>
                      <p className="font-semibold text-gray-800 dark:text-gray-200 truncate">
                        {viewingReschedule.fatura?.reagendamento_por_nome || 'Desconhecido'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase mb-1">Motivo do Reagendamento</h4>
                    <div className="bg-gray-50 dark:bg-gray-700/30 p-3 rounded-xl border dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 min-h-[60px] whitespace-pre-wrap">
                      {viewingReschedule.fatura?.motivo_reagendamento || 'Nenhum motivo informado.'}
                    </div>
                  </div>

                  {viewingReschedule.fatura?.comprovante_reagendamento_url && (
                    <div>
                      <h4 className="text-xs font-bold text-gray-400 uppercase mb-1">Prova do Reagendamento</h4>
                      <div className="relative border dark:border-gray-700 rounded-xl overflow-hidden bg-gray-900 group aspect-video flex items-center justify-center max-h-[220px]">
                        <img
                          src={viewingReschedule.fatura.comprovante_reagendamento_url}
                          alt="Comprovante de Reagendamento"
                          className="max-w-full max-h-full object-contain"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <a
                            href={viewingReschedule.fatura.comprovante_reagendamento_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors text-xs font-bold flex items-center gap-1"
                          >
                            <Info size={14} /> Abrir em nova aba
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-gray-800/50 flex border-t dark:border-gray-700">
                <button
                  onClick={() => setViewingReschedule(null)}
                  className="w-full py-2.5 bg-[#5F6C37] text-white rounded-xl font-bold hover:bg-[#5F6C37]/90 transition-all text-sm shadow-md shadow-[#5F6C37]/20 flex items-center justify-center gap-1"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default PendentesCalculoModal;
